// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/**
 * AI Assistant — page layout and composition.
 *
 * Rules this file follows (see 0_web_labelflow/DESIGN_GUIDE.md):
 * - Every colour is a token. Both themes come free; nothing is hardcoded.
 * - Motion is 120-180ms and eased, never overshooting. The previous stylesheet
 *   used \`cubic-bezier(0.34, 1.56, 0.64, 1)\` on cards, hover and messages,
 *   which is the spring the guide's "nothing bounces" rule exists to forbid.
 * - Full-height regions use \`100dvh\` with a \`100vh\` line above as the fallback,
 *   so mobile browser chrome cannot clip the composer off the bottom.
 * - Type and spacing are \`clamp()\`ed rather than stepped through breakpoints;
 *   media queries are reserved for real reflow (the sidebar becoming a drawer).
 *
 * The 58px below is the header's \`min-height\` from styles/responsive.css. It is
 * subtracted along with the container gutter so the composer lands exactly on
 * the fold instead of hanging below it.
 */

.lf-assistant {
  --lf-gutter: clamp(0.75rem, 0.4rem + 2vw, 1.5rem);
  --lf-sidebar-w: 17rem;
  --lf-context-w: 21rem;
  /* The reading measure for the conversation. Wider than a chat bubble because
     assistant answers carry headings, bullets and code. */
  --lf-measure: 46rem;

  position: relative;
  display: flex;
  align-items: stretch;
  gap: 0;
  /* Height comes from the shell (.lf-fill-viewport in responsive.css). The
     58px here was the header's assumed size, and it is 57px — close enough to
     look right and still wrong by a pixel at every window size. */
  min-height: 30rem;
  overflow: hidden;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

/* ==========================================================================
   Sidebar — conversation list
   ========================================================================== */

.lf-assistant__sidebar {
  flex: 0 0 var(--lf-sidebar-w);
  width: var(--lf-sidebar-w);
  min-width: 0;
  border-right: 1px solid var(--border);
  background: var(--page-bg-subtle);
  transition: flex-basis var(--transition), width var(--transition);
  overflow: hidden;
}

.lf-assistant__sidebar.is-collapsed {
  flex-basis: 0;
  width: 0;
  border-right-color: transparent;
}

.lf-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: var(--lf-sidebar-w);
  min-height: 0;
}

.lf-sidebar__top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border);
}

.lf-sidebar__new {
  flex: 1 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.25rem;
  padding: 0 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-foreground);
  background: var(--primary);
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.lf-sidebar__new:hover {
  background: var(--primary-hover);
}

.lf-sidebar__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  color: var(--muted-foreground);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
}

.lf-sidebar__search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem 0.75rem 0.25rem;
  padding: 0 0.625rem;
  background: var(--card);
  border: 1px solid var(--input);
  border-radius: var(--radius);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.lf-sidebar__search:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--ring);
}

.lf-sidebar__search > i {
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

/*
 * The chrome belongs to the WRAPPER above, never to this input.
 *
 * theme-overrides.css styles every \`input:focus\` with \`!important\` — border,
 * 3px ring, background — so a page-level \`border: none\` here loses silently and
 * the input paints a second bordered box, with a second ring, INSIDE the
 * wrapper's. That is the doubled highlight this class exists to stop: the
 * markup carries \`lf-field-bare\`, which is that file's supported opt-out.
 * Measured on the live page before the fix: wrapper AND input both at
 * \`1px rgb(16,185,129)\` + \`rgba(52,211,153,.4) 0 0 0 3px\`.
 */
.lf-sidebar__search input {
  flex: 1 1;
  min-width: 0;
  height: 2.125rem;
  font-size: 0.8125rem;
  color: var(--foreground);
}

/* The UA's own clear affordance would sit beside ours. */
.lf-sidebar__search input::-webkit-search-cancel-button {
  display: none;
}

.lf-sidebar__search button {
  display: inline-flex;
  padding: 0.25rem;
  font-size: 0.7rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  cursor: pointer;
}

.lf-sidebar__list {
  flex: 1 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem 0.5rem 1rem;
}

.lf-sidebar__none {
  margin: 1.5rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--muted-foreground);
}

.lf-sidebar__group + .lf-sidebar__group {
  margin-top: 0.75rem;
}

.lf-sidebar__group-title {
  margin: 0 0 0.25rem;
  padding: 0 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}

.lf-sidebar__threads {
  margin: 0;
  padding: 0;
  list-style: none;
}

.lf-thread-row {
  position: relative;
  display: flex;
  align-items: center;
  border-radius: var(--radius-sm);
  transition: background var(--transition-fast);
}

.lf-thread-row:hover {
  background: var(--card-hover);
}

.lf-thread-row--active {
  background: var(--primary-soft);
}

.lf-thread-row__main {
  flex: 1 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.5rem;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
}

.lf-thread-row__icon {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.lf-thread-row--active .lf-thread-row__icon {
  color: var(--primary);
}

.lf-thread-row__text {
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  min-width: 0;
}

.lf-thread-row__title {
  overflow: hidden;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--foreground);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-thread-row__meta {
  font-size: 0.6875rem;
  color: var(--muted-foreground);
}

.lf-thread-row__tools {
  display: flex;
  flex-shrink: 0;
  gap: 0.125rem;
  padding-right: 0.25rem;
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.lf-thread-row:hover .lf-thread-row__tools,
.lf-thread-row:focus-within .lf-thread-row__tools {
  opacity: 1;
}

.lf-thread-row__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  font-size: 0.6875rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.lf-thread-row__tool:hover {
  color: var(--foreground);
  background: var(--secondary);
}

.lf-thread-row__tool--danger:hover {
  color: var(--destructive);
  background: var(--destructive-soft);
}

/*
 * \`width: 100%\` PLUS horizontal margins overflowed the row by exactly those
 * margins, and the 3px focus ring the global layer adds pushed it further out
 * of a 17rem sidebar. As a flex child it fills the space it actually has.
 *
 * The border is neutral here, not \`--primary\`: this field was drawn in the
 * focus colour even while blurred, so a row being renamed looked focused when
 * focus had moved elsewhere. Focus chrome comes from the global layer.
 */
.lf-thread-row__rename {
  flex: 1 1;
  min-width: 0;
  margin: 0.25rem 0.5rem;
  padding: 0.375rem 0.5rem;
  font-size: 0.8125rem;
  color: var(--foreground);
  background: var(--card);
  border: 1px solid var(--input);
  border-radius: var(--radius-sm);
}

.lf-sidebar__foot {
  flex-shrink: 0;
  padding: 0.625rem 0.75rem;
  border-top: 1px solid var(--border);
}

.lf-sidebar__clear {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.4375rem 0.5rem;
  font-size: 0.75rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.lf-sidebar__clear:hover {
  color: var(--destructive);
  background: var(--destructive-soft);
}

/* ==========================================================================
   Main column
   ========================================================================== */

.lf-assistant__main {
  flex: 1 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--card);
}

.lf-topbar {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.375rem;
  padding: 0.625rem clamp(0.625rem, 2vw, 1rem);
  border-bottom: 1px solid var(--border);
}

.lf-topbar__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  font-size: 0.8125rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.lf-topbar__icon:hover:not(:disabled) {
  color: var(--foreground);
  background: var(--secondary);
}

.lf-topbar__icon:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.lf-topbar__icon--active {
  color: var(--primary);
  background: var(--primary-soft);
}

.lf-topbar__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.lf-topbar__heading {
  margin: 0;
  overflow: hidden;
  font-size: clamp(0.875rem, 0.8rem + 0.4vw, 1rem);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--foreground);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-topbar__spacer {
  flex: 1 1;
}

.lf-topbar__menu {
  position: relative;
}

.lf-topbar__dropdown {
  position: absolute;
  top: calc(100% + 0.375rem);
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 12rem;
  padding: 0.25rem;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}

.lf-topbar__dropdown button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  color: var(--popover-foreground);
  text-align: left;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.lf-topbar__dropdown button:hover {
  background: var(--secondary);
}

.lf-topbar__dropdown button i {
  width: 1rem;
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.lf-topbar__new {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.4375rem;
  min-height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--secondary-foreground);
  background: var(--secondary);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.lf-topbar__new:hover {
  background: var(--secondary-hover);
  border-color: var(--border-strong);
}

/* ---- error banner -------------------------------------------------------- */

.lf-assistant__banner {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.625rem;
  padding: 0.625rem clamp(0.75rem, 2vw, 1rem);
  font-size: 0.8125rem;
  color: var(--destructive);
  background: var(--destructive-soft);
  border-bottom: 1px solid var(--border);
}

.lf-assistant__banner > span {
  flex: 1 1;
  min-width: 0;
}

.lf-assistant__banner button {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--destructive);
  background: transparent;
  border: 1px solid currentColor;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.lf-assistant__banner button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lf-assistant__banner-close {
  border-color: transparent !important;
}

/* ---- body --------------------------------------------------------------- */

.lf-assistant__body {
  flex: 1 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.lf-assistant__welcome-scroll {
  flex: 1 1;
  min-height: 0;
  overflow-y: auto;
}

/* ==========================================================================
   Welcome / empty state
   ========================================================================== */

.lf-welcome {
  max-width: var(--lf-measure);
  margin: 0 auto;
  padding: clamp(1.5rem, 4vh, 3rem) clamp(1rem, 3vw, 1.75rem) 1.5rem;
}

.lf-welcome__intro {
  text-align: center;
}

.lf-welcome__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin-bottom: 0.875rem;
  font-size: 1.125rem;
  color: var(--primary);
  background: var(--primary-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.lf-welcome__title {
  margin: 0 0 0.5rem;
  font-size: clamp(1.25rem, 1rem + 1.4vw, 1.75rem);
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: var(--foreground);
}

.lf-welcome__subtitle {
  max-width: 34rem;
  margin: 0 auto;
  font-size: clamp(0.8125rem, 0.78rem + 0.2vw, 0.9375rem);
  line-height: 1.6;
  color: var(--muted-foreground);
}

.lf-welcome__context {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin: 1rem 0 0;
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.lf-welcome__context i {
  color: var(--info);
}

.lf-welcome__context--loading i {
  color: var(--muted-foreground);
}

/* Suggestions: auto-fill so 320px gets one column and a wide screen gets two,
   without a breakpoint deciding it. */
.lf-welcome__suggestions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(18rem, 100%), 1fr));
  gap: 0.625rem;
  margin-top: clamp(1.25rem, 3vh, 2rem);
}

.lf-suggest {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem;
  text-align: left;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  transition: border-color var(--transition-fast), background var(--transition-fast),
    box-shadow var(--transition-fast);
}

.lf-suggest:hover {
  background: var(--card-hover);
  border-color: var(--border-strong);
  box-shadow: var(--shadow-sm);
}

.lf-suggest:focus-visible {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--ring);
}

.lf-suggest--context {
  border-color: var(--primary-soft);
  background: var(--primary-soft);
}

.lf-suggest--context:hover {
  border-color: var(--primary);
}

.lf-suggest__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  font-size: 0.8125rem;
  color: var(--primary);
  background: var(--accent);
  border-radius: var(--radius-sm);
}

.lf-suggest--context .lf-suggest__icon {
  background: var(--card);
}

.lf-suggest__text {
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
  min-width: 0;
}

.lf-suggest__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
}

.lf-suggest__prompt {
  /* Two lines of the real question, so the card shows what will be sent. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.lf-suggest__tag {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.0625rem 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--primary);
  background: var(--card);
  border-radius: var(--radius-full);
}

.lf-welcome__links {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: clamp(1.25rem, 3vh, 2rem);
  padding-top: 1rem;
  border-top: 1px solid var(--border);
}

.lf-welcome__links-label {
  margin-right: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}

.lf-welcome__link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3125rem 0.625rem;
  font-size: 0.75rem;
  color: var(--muted-foreground);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: color var(--transition-fast), border-color var(--transition-fast),
    background var(--transition-fast);
}

.lf-welcome__link:hover {
  color: var(--primary);
  background: var(--accent);
  border-color: var(--primary);
}

/* ==========================================================================
   Thread
   ========================================================================== */

.lf-thread {
  position: relative;
  flex: 1 1;
  min-height: 0;
  display: flex;
}

.lf-thread__scroll {
  flex: 1 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.lf-thread__inner {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: var(--lf-measure);
  margin: 0 auto;
  padding: clamp(1rem, 3vh, 1.75rem) clamp(1rem, 3vw, 1.75rem) 1rem;
}

.lf-thread__jump {
  position: absolute;
  right: 1rem;
  bottom: 0.75rem;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  font-size: 0.75rem;
  color: var(--foreground);
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  box-shadow: var(--shadow-md);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.lf-thread__jump:hover {
  background: var(--secondary);
}

/* ---- messages ----------------------------------------------------------- */

.lf-msg {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* One short, non-overshooting entrance. The old sheet alternated two
     different slide-ins keyed off :nth-child(odd/even), so the animation a
     message got depended on its position and flipped as the list grew. */
  animation: lf-msg-in var(--transition-slow) ease-out both;
}

@keyframes lf-msg-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* -- the user's turn -- */

.lf-msg--user {
  align-items: flex-end;
}

.lf-msg__bubble {
  max-width: min(85%, 34rem);
  padding: 0.625rem 0.875rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--foreground);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: var(--secondary);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg) var(--radius-lg) var(--radius-sm) var(--radius-lg);
}

/* -- the assistant's turn -- */

.lf-msg__meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.lf-msg__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  font-size: 0.6875rem;
  color: var(--primary);
  background: var(--primary-soft);
  border-radius: var(--radius-sm);
}

.lf-msg__author {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--muted-foreground);
}

.lf-msg__badge {
  font-size: 0.625rem !important;
}

.lf-msg__body {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--foreground);
  overflow-wrap: anywhere;
}

.lf-msg__body > :first-child {
  margin-top: 0;
}

.lf-msg__body > :last-child {
  margin-bottom: 0;
}

.lf-msg__body p {
  margin: 0 0 0.75rem;
}

/* The streaming caret: a pseudo-element on the body, so no extra node can be
   orphaned by a chunk landing mid-render. */
.lf-msg__body--streaming::after {
  content: '';
  display: inline-block;
  width: 0.5rem;
  height: 1.05em;
  margin-left: 0.125rem;
  vertical-align: text-bottom;
  background: var(--primary);
  border-radius: 1px;
  animation: lf-caret 1s steps(2, start) infinite;
}

@keyframes lf-caret {
  50% {
    opacity: 0;
  }
}

.lf-msg--error .lf-msg__body {
  color: var(--destructive);
}

.lf-msg__error-text {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  background: var(--destructive-soft);
  border-radius: var(--radius);
}

.lf-msg__notice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  padding: 0.5rem 0.625rem;
  font-size: 0.75rem;
  color: var(--warning);
  background: var(--warning-soft);
  border-radius: var(--radius-sm);
}

/* -- typing indicator -- */

.lf-msg__typing {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.25rem 0;
}

.lf-msg__typing span:not(.lf-msg__typing-text) {
  width: 0.3125rem;
  height: 0.3125rem;
  background: var(--primary);
  border-radius: 50%;
  animation: lf-typing 1.2s ease-in-out infinite both;
}

.lf-msg__typing span:nth-child(1) {
  animation-delay: -0.24s;
}
.lf-msg__typing span:nth-child(2) {
  animation-delay: -0.12s;
}

.lf-msg__typing-text {
  margin-left: 0.375rem;
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

@keyframes lf-typing {
  0%,
  70%,
  100% {
    opacity: 0.35;
    transform: scale(0.8);
  }
  35% {
    opacity: 1;
    transform: scale(1);
  }
}

/* -- per-message actions -- */

.lf-msg__actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  /* Hidden until the message is hovered or focused, so the prose stays clean;
     always visible on touch, where there is no hover to reveal them. */
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.lf-msg:hover .lf-msg__actions,
.lf-msg:focus-within .lf-msg__actions {
  opacity: 1;
}

@media (hover: none) {
  .lf-msg__actions {
    opacity: 1;
  }
}

.lf-msg__actions--user {
  justify-content: flex-end;
}

.lf-msg__action {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--muted-foreground);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast),
    border-color var(--transition-fast);
}

.lf-msg__action:hover:not(:disabled) {
  color: var(--foreground);
  background: var(--secondary);
  border-color: var(--border);
}

.lf-msg__action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* -- inline edit -- */

.lf-msg--editing {
  align-items: stretch;
}

/*
 * One surface, one border — the same contract as the composer.
 *
 * This used to be a permanently primary-bordered card wrapping a textarea that
 * drew its OWN primary border and ring on focus: two concentric green boxes
 * with a ring between them. The container now owns the chrome and lights up on
 * \`:focus-within\`; the textarea is bare (\`lf-field-bare\` in the markup).
 */
.lf-msg__edit {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--card);
  border: 1px solid var(--input);
  border-radius: var(--radius);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.lf-msg__edit:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--ring);
}

.lf-msg__edit-input {
  width: 100%;
  font-family: inherit;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--foreground);
  padding: 0.25rem;
  resize: vertical;
}

.lf-msg__edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.lf-msg__edit-cancel,
.lf-msg__edit-save {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.lf-msg__edit-cancel {
  color: var(--muted-foreground);
  background: transparent;
  border: 1px solid var(--border);
}

.lf-msg__edit-cancel:hover {
  background: var(--secondary);
}

.lf-msg__edit-save {
  color: var(--primary-foreground);
  background: var(--primary);
  border: none;
}

.lf-msg__edit-save:hover:not(:disabled) {
  background: var(--primary-hover);
}

.lf-msg__edit-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ==========================================================================
   Markdown inside a reply
   ========================================================================== */

.lf-md-link {
  color: var(--primary);
  font-weight: 500;
  text-decoration: none;
  border-bottom: 1px solid var(--primary-soft);
  transition: color var(--transition-fast), border-color var(--transition-fast);
}

.lf-md-link:hover {
  color: var(--primary-hover);
  border-bottom-color: var(--primary);
}

.lf-md-link__chevron {
  margin-left: 0.1875rem;
  font-size: 0.75em;
  opacity: 0.7;
}

.lf-md-h {
  margin: 1.25rem 0 0.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--foreground);
}

.lf-md-h--1 {
  font-size: 1.0625rem;
}
.lf-md-h--2 {
  font-size: 1rem;
}
.lf-md-h--3 {
  font-size: 0.9375rem;
}
.lf-md-h--4 {
  font-size: 0.875rem;
}

.lf-md-list {
  margin: 0 0 0.75rem;
  padding-left: 1.25rem;
}

.lf-md-list li {
  margin-bottom: 0.3125rem;
}

.lf-md-list li::marker {
  color: var(--muted-foreground);
}

.lf-md-quote {
  margin: 0 0 0.75rem;
  padding: 0.5rem 0.875rem;
  color: var(--muted-foreground);
  border-left: 2px solid var(--border-strong);
  background: var(--page-bg-subtle);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}

.lf-md-rule {
  margin: 1.25rem 0;
  border: none;
  border-top: 1px solid var(--border);
}

.lf-md-inline-code {
  padding: 0.0625rem 0.3125rem;
  font-family: var(--font-mono);
  font-size: 0.875em;
  color: var(--accent-foreground);
  background: var(--accent);
  border-radius: var(--radius-sm);
}

.lf-md-code {
  position: relative;
  margin: 0 0 0.875rem;
}

.lf-md-code__pre {
  margin: 0;
  padding: 0.875rem 1rem;
  /* A long line scrolls inside the block; the page itself must never scroll
     sideways (DESIGN_GUIDE §6). */
  overflow-x: auto;
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--foreground);
  background: var(--page-bg-subtle);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.lf-md-code__pre code {
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  background: none;
  padding: 0;
}

.lf-md-code__copy {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  font-size: 0.6875rem;
  color: var(--muted-foreground);
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  opacity: 0;
  transition: opacity var(--transition-fast), color var(--transition-fast);
}

.lf-md-code:hover .lf-md-code__copy,
.lf-md-code__copy:focus-visible {
  opacity: 1;
}

.lf-md-code__copy:hover {
  color: var(--foreground);
}

@media (hover: none) {
  .lf-md-code__copy {
    opacity: 1;
  }
}

/* ==========================================================================
   Composer
   ========================================================================== */

.lf-assistant__composer {
  flex-shrink: 0;
  padding: 0.75rem clamp(1rem, 3vw, 1.75rem);
  padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
  border-top: 1px solid var(--border);
  background: var(--card);
}

.lf-composer {
  max-width: var(--lf-measure);
  margin: 0 auto;
}

/*
 * The surface owns the border and the focus ring, so an attached dataset and
 * the text box read as ONE control rather than two stacked boxes.
 */
.lf-composer__surface {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.5rem 0.4375rem;
  background: var(--card);
  border: 1px solid var(--input);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.lf-composer__surface:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--ring);
}

.lf-composer__surface--invalid,
.lf-composer__surface--invalid:focus-within {
  border-color: var(--destructive);
  box-shadow: none;
}

.lf-composer__tray {
  padding-bottom: 0.125rem;
}

/* The file input is driven by the paperclip button; it is never shown. */
.lf-composer__file {
  display: none;
}

/*
 * The control rail, under the text rather than beside it. Buttons flanking the
 * textarea squeezed a long question into a narrow channel; full-width text is
 * the point of this layout.
 */
.lf-composer__rail {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-height: 2rem;
}

.lf-composer__attach {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  font-size: 0.875rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.lf-composer__attach:hover:not(:disabled) {
  color: var(--foreground);
  background: var(--secondary);
}

.lf-composer__attach.is-on {
  color: var(--primary);
  background: var(--primary-soft);
}

.lf-composer__attach:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/*
 * One-tap prompts, ABOVE the surface: they are things to ask before typing, and
 * under the input they made a third stacked band.
 */
.lf-composer__seeds {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}

.lf-composer__seed {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3125rem 0.6875rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--foreground);
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: border-color var(--transition-fast), background var(--transition-fast),
    color var(--transition-fast);
}

.lf-composer__seed:hover {
  background: var(--primary-soft);
  border-color: var(--primary);
  color: var(--primary);
}

.lf-composer__seed i {
  font-size: 0.6875rem;
  color: var(--primary);
}

/* ---- the staged dataset chip ---- */

/*
 * flex-start, not center: the metadata wraps to three or four lines at phone
 * width, and centring left the file icon and the Replace/close buttons floating
 * halfway down an otherwise top-aligned block.
 */
.lf-attach {
  position: relative; /* anchors .lf-attach__bar along the bottom edge */
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  background: var(--secondary);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden; /* so the bar is clipped to the rounded corners */
}

/*
 * The reading bar.
 *
 * INDETERMINATE ON PURPOSE. Nothing in the read reports a real percentage --
 * JSZip.loadAsync exposes no progress callback -- and a fabricated percentage
 * that parks at 90% is a worse lie than no number at all. What this has to
 * communicate is only "still working", which is exactly the thing a static
 * spinner cannot: the named phase beside it carries the detail.
 */
.lf-attach__bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: var(--border);
  overflow: hidden;
}

.lf-attach__bar::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40%;
  background: var(--primary);
  animation: lf-attach-sweep 1.1s ease-in-out infinite;
}

@keyframes lf-attach-sweep {
  from { left: -40%; }
  to { left: 100%; }
}

/* A sweeping bar is motion; honour the user's setting and hold it still. */
@media (prefers-reduced-motion: reduce) {
  .lf-attach__bar::after {
    animation: none;
    left: 0;
    width: 100%;
    opacity: 0.5;
  }
}

.lf-attach--bad {
  background: var(--destructive-soft);
  border-color: var(--destructive);
}

.lf-attach__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  font-size: 0.875rem;
  color: var(--primary);
  background: var(--card);
  border-radius: var(--radius);
}

.lf-attach--bad .lf-attach__icon {
  color: var(--destructive);
}

.lf-attach__body {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1 1;
}

.lf-attach__name {
  overflow: hidden;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*
 * Five metadata chunks ran together into one unreadable line, because the only
 * thing between them was a gap. A middot between plain items gives the row
 * rhythm; the layout pill is skipped, since a dot butted against a pill reads
 * as punctuation that lost its word.
 */
.lf-attach__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
  font-size: 0.6875rem;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.lf-attach__meta > * + *:not(.lf-attach__tag)::before {
  content: '\\00b7';
  margin-right: 0.375rem;
  color: var(--border-strong);
}

.lf-attach--bad .lf-attach__meta {
  color: var(--destructive);
}

/* The one highlighted fact: whether the archive's layout was understood. */
.lf-attach__tag {
  padding: 0.0625rem 0.4375rem;
  font-weight: 600;
  color: var(--primary);
  background: var(--primary-soft);
  border-radius: var(--radius-full);
}

.lf-attach__action {
  flex-shrink: 0;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--muted-foreground);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  cursor: pointer;
}

.lf-attach__action:hover:not(:disabled) {
  color: var(--foreground);
  border-color: var(--border-strong);
}

.lf-attach__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  font-size: 0.75rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-full);
  cursor: pointer;
}

.lf-attach__close:hover:not(:disabled) {
  color: var(--destructive);
  background: var(--destructive-soft);
}

.lf-composer__input {
  width: 100%;
  min-width: 0;
  /* Grown to fit by the component; this is the single-row starting point. */
  height: 2.5rem;
  max-height: 12.5rem;
  padding: 0.5rem 0.375rem 0.25rem;
  font-family: inherit;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--foreground);
  resize: none;
}

.lf-composer__input::placeholder {
  color: var(--muted-foreground);
}

/*
 * Send is a filled circle, matching the pill language of the rail rather than
 * the rounded square it used to be inside an --radius-xl surface.
 */
.lf-composer__send,
.lf-composer__stop {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  font-size: 0.8125rem;
  border: none;
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast),
    box-shadow var(--transition-fast), transform var(--transition-fast);
}

.lf-composer__send {
  color: var(--primary-foreground);
  background: var(--primary);
}

.lf-composer__send:hover:not(:disabled) {
  background: var(--primary-hover);
  box-shadow: var(--shadow-glow);
}

.lf-composer__send:active:not(:disabled) {
  transform: scale(0.94);
}

.lf-composer__send:disabled {
  color: var(--muted-foreground);
  background: var(--secondary);
  cursor: not-allowed;
}

.lf-composer__stop {
  color: var(--destructive-foreground);
  background: var(--destructive);
}

.lf-composer__stop:hover {
  background: var(--destructive-hover);
}

.lf-composer__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 1;
  min-width: 0;
  height: 2rem;
  padding: 0 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--muted-foreground);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast),
    border-color var(--transition-fast);
}

.lf-composer__chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-composer__chip:hover:not(:disabled) {
  color: var(--foreground);
  border-color: var(--border-strong);
}

.lf-composer__chip--on {
  color: var(--primary);
  background: var(--primary-soft);
  border-color: transparent;
}

.lf-composer__chip--on:hover:not(:disabled) {
  color: var(--primary);
  border-color: var(--primary);
}

.lf-composer__chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lf-composer__spacer {
  flex: 1 1;
  min-width: 0.25rem;
}

.lf-composer__count {
  flex-shrink: 0;
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
  color: var(--muted-foreground);
}

.lf-composer__count--over {
  font-weight: 600;
  color: var(--destructive);
}

.lf-composer__hint {
  flex-shrink: 0;
  font-size: 0.6875rem;
  color: var(--muted-foreground);
  white-space: nowrap;
}

.lf-composer__shortcuts {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  font-size: 0.75rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.lf-composer__shortcuts:hover {
  color: var(--foreground);
  background: var(--secondary);
}

/* ==========================================================================
   Workspace context panel
   ========================================================================== */

.lf-assistant__context {
  flex: 0 0 var(--lf-context-w);
  width: var(--lf-context-w);
  min-width: 0;
  border-left: 1px solid var(--border);
  background: var(--page-bg-subtle);
  overflow: hidden;
}

.lf-context {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.lf-context__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border);
}

.lf-context__title {
  display: flex;
  align-items: center;
  gap: 0.4375rem;
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
}

.lf-context__title i {
  font-size: 0.75rem;
  color: var(--info);
}

.lf-context__head-tools {
  display: flex;
  gap: 0.125rem;
}

.lf-context__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  font-size: 0.6875rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.lf-context__tool:hover:not(:disabled) {
  color: var(--foreground);
  background: var(--secondary);
}

.lf-context__switch {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-shrink: 0;
  margin: 0;
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border);
}

.lf-context__switch input {
  flex-shrink: 0;
  width: 0.9375rem;
  height: 0.9375rem;
  margin-top: 0.125rem;
  accent-color: var(--primary);
}

.lf-context__switch-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--foreground);
}

.lf-context__switch-hint {
  display: block;
  margin-top: 0.125rem;
  font-size: 0.6875rem;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.lf-context__stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.0625rem;
  flex-shrink: 0;
  margin: 0;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border);
}

.lf-context__stats > div {
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
}

.lf-context__stats dt {
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}

.lf-context__stats dd {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 0.9375rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--foreground);
}

.lf-context__warn {
  color: var(--warning) !important;
}

.lf-context__list {
  flex: 1 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem;
}

.lf-context__none {
  padding: 1.5rem 0.75rem;
  text-align: center;
}

.lf-context__none p {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  line-height: 1.6;
  color: var(--muted-foreground);
}

.lf-context__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary-foreground);
  background: var(--primary);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.lf-context__row {
  padding: 0.625rem;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}

.lf-context__row + .lf-context__row {
  margin-top: 0.375rem;
}

.lf-context__row-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.375rem;
}

.lf-context__row-name {
  overflow: hidden;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-context__row-version {
  flex-shrink: 0;
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  color: var(--muted-foreground);
}

.lf-context__row-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.375rem;
}

.lf-context__row-tags .lf-badge {
  font-size: 0.625rem !important;
}

.lf-context__row-meta {
  margin: 0.375rem 0 0;
  font-size: 0.6875rem;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.lf-context__row-warn {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  margin: 0.3125rem 0 0;
  font-size: 0.6875rem;
  color: var(--warning);
}

.lf-context__row-ask {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--primary);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.lf-context__row-ask:hover {
  background: var(--accent);
  border-color: var(--primary);
}

/* ==========================================================================
   Shortcuts dialog
   ========================================================================== */

.lf-modal {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: var(--overlay);
  animation: lf-fade var(--transition) ease-out both;
}

@keyframes lf-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.lf-modal__card {
  width: 100%;
  max-width: 26rem;
  max-height: 85vh; /* fallback */
  max-height: 85dvh;
  overflow-y: auto;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.lf-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border);
}

.lf-modal__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--popover-foreground);
}

.lf-modal__title i {
  font-size: 0.8125rem;
  color: var(--muted-foreground);
}

.lf-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  font-size: 0.8125rem;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.lf-modal__close:hover {
  color: var(--foreground);
  background: var(--secondary);
}

.lf-modal__list {
  margin: 0;
  padding: 0.5rem;
  list-style: none;
}

.lf-modal__list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.4375rem 0.5rem;
  border-radius: var(--radius-sm);
}

.lf-modal__list li:hover {
  background: var(--secondary);
}

.lf-modal__desc {
  font-size: 0.8125rem;
  color: var(--popover-foreground);
}

.lf-modal__keys {
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
}

.lf-modal__keys kbd {
  min-width: 1.5rem;
  padding: 0.125rem 0.375rem;
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  text-align: center;
  color: var(--foreground);
  background: var(--page-bg-subtle);
  border: 1px solid var(--border);
  border-bottom-width: 2px;
  border-radius: var(--radius-sm);
}

/* ==========================================================================
   Reflow
   ========================================================================== */

/* The context panel stops being affordable before the sidebar does: it is the
   least essential of the three columns, so it overlays instead of squeezing
   the conversation below a readable width. */
@media (max-width: 1200px) {
  .lf-assistant {
    --lf-context-w: 18rem;
  }
}

@media (max-width: 992px) {
  .lf-assistant__context {
    position: absolute;
    inset: 0 0 0 auto;
    z-index: 30;
    width: min(20rem, 88vw);
    flex-basis: auto;
    box-shadow: var(--shadow-lg);
  }
}

/* 768px matches .lf-hide-md / .lf-only-md in styles/responsive.css, so the
   hamburger appears in exactly the same breath as the drawer takes over. */
@media (max-width: 768px) {
  .lf-assistant {
    border-radius: var(--radius);
  }

  .lf-assistant__sidebar {
    position: absolute;
    inset: 0 auto 0 0;
    z-index: 40;
    flex-basis: auto;
    width: min(17rem, 84vw);
    transform: translateX(-100%);
    transition: transform var(--transition);
    box-shadow: var(--shadow-lg);
  }

  .lf-assistant__sidebar.is-open {
    transform: none;
  }

  /* The desktop collapse must not survive into the drawer, or the drawer opens
     at zero width. */
  .lf-assistant__sidebar.is-collapsed {
    flex-basis: auto;
    width: min(17rem, 84vw);
    border-right-color: var(--border);
  }

  .lf-sidebar {
    width: 100%;
  }

  .lf-assistant__backdrop {
    position: absolute;
    inset: 0;
    z-index: 35;
    background: var(--overlay);
  }
}

@media (max-width: 480px) {
  .lf-msg__bubble {
    max-width: 100%;
  }

  .lf-welcome__suggestions {
    grid-template-columns: 1fr;
  }
}

/* ==========================================================================
   Dataset drop target

   Wraps the conversation only. It takes over the flex role \`.lf-assistant__body\`
   had in the column, and is the positioning context for the overlay.
   ========================================================================== */

.lf-drop {
  position: relative;
  flex: 1 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.lf-drop__overlay {
  position: absolute;
  inset: 0;
  z-index: 28;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: var(--overlay);
  backdrop-filter: blur(2px);
}

.lf-drop__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 2.5rem;
  text-align: center;
  color: var(--foreground);
  background: var(--card);
  border: 2px dashed var(--primary);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
}

.lf-drop__card i {
  font-size: 1.75rem;
  color: var(--primary);
}

.lf-drop__card strong {
  font-size: 1rem;
}

.lf-drop__card span {
  font-size: 0.8125rem;
  color: var(--muted-foreground);
}

/* ==========================================================================
   Build card

   The only surface in the assistant that can create anything. It is deliberately
   quiet until it has run: everything on it is a proposal.
   ========================================================================== */

.lf-msg--card {
  max-width: var(--lf-measure);
  margin: 0 auto 1.25rem;
  width: 100%;
}

.lf-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
}

.lf-card__head {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.lf-card__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  font-size: 0.875rem;
  color: var(--primary-foreground);
  background: var(--primary);
  border-radius: var(--radius);
}

.lf-card__heading {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1;
}

.lf-card__heading strong {
  font-size: 0.9375rem;
  color: var(--foreground);
}

.lf-card__sub {
  overflow: hidden;
  font-size: 0.75rem;
  color: var(--muted-foreground);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-card__mode {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  padding: 0.25rem 0.625rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--primary);
  background: var(--primary-soft);
  border-radius: var(--radius-full);
}

.lf-card__facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
  gap: 0.625rem;
  margin: 0;
  padding: 0.75rem;
  background: var(--secondary);
  border-radius: var(--radius-lg);
}

.lf-card__facts div {
  min-width: 0;
}

.lf-card__facts dt {
  margin: 0 0 0.125rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--muted-foreground);
}

.lf-card__facts dd {
  margin: 0;
  overflow: hidden;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--foreground);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-card__hint {
  margin: 0;
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.lf-card__classes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
}

.lf-card__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.1875rem 0.5rem;
  font-size: 0.75rem;
  color: var(--foreground);
  background: var(--secondary);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
}

.lf-card__chip em {
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  font-style: normal;
  color: var(--muted-foreground);
}

/*
 * \`--card\`, not \`--background\`: the global layer forces \`background: var(--card)\`
 * on \`input:focus\` with \`!important\`, so declaring anything else here made the
 * field visibly change shade the moment it was clicked. Matching it means no
 * flip. The focus border and ring also come from that layer, which is why there
 * is no \`:focus\` rule here — the one that used to be here was dead.
 */
.lf-card__rename {
  width: 8rem;
  padding: 0.25rem 0.5rem;
  font-family: inherit;
  font-size: 0.75rem;
  color: var(--foreground);
  background: var(--card);
  border: 1px solid var(--input);
  border-radius: var(--radius-sm);
}

.lf-card__mini {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.1875rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--muted-foreground);
  background: transparent;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-full);
  cursor: pointer;
}

.lf-card__mini:hover {
  color: var(--primary);
  border-color: var(--primary);
}

.lf-card__notes {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.lf-card__notes li {
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.lf-card__notes i {
  color: var(--info);
}

.lf-card__error {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin: 0;
  padding: 0.5rem 0.625rem;
  font-size: 0.75rem;
  color: var(--destructive);
  background: var(--destructive-soft);
  border-radius: var(--radius);
}

.lf-card__result {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--success);
  background: var(--secondary);
  border-radius: var(--radius);
}

.lf-card__result--warn {
  color: var(--warning);
  background: var(--warning-soft);
}

.lf-card__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.lf-card__run,
.lf-card__stop {
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.4375rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.lf-card__run {
  color: var(--primary-foreground);
  background: var(--primary);
}

.lf-card__run:hover {
  background: var(--primary-hover);
}

.lf-card__stop {
  color: var(--destructive-foreground);
  background: var(--destructive);
}

.lf-card__stop:hover {
  background: var(--destructive-hover);
}

.lf-card__link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--primary);
  background: transparent;
  border: none;
  cursor: pointer;
}

.lf-card__link:hover {
  text-decoration: underline;
}

/* ---- the five steps ---- */

.lf-steps {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0.625rem;
  list-style: none;
  background: var(--secondary);
  border-radius: var(--radius-lg);
}

.lf-steps__row {
  display: grid;
  grid-template-columns: 1rem 1fr auto;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.lf-steps__row--done .lf-steps__icon {
  color: var(--success);
}

.lf-steps__row--running {
  color: var(--foreground);
}

.lf-steps__row--running .lf-steps__icon {
  color: var(--primary);
}

.lf-steps__row--failed {
  color: var(--destructive);
}

.lf-steps__row--failed .lf-steps__icon {
  color: var(--destructive);
}

.lf-steps__row--skipped {
  opacity: 0.55;
}

.lf-steps__icon {
  font-size: 0.75rem;
  text-align: center;
}

.lf-steps__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lf-steps__state {
  font-family: var(--font-mono);
  font-size: 0.6875rem;
}

.lf-steps__bar {
  grid-column: 2 / -1;
  height: 3px;
  overflow: hidden;
  background: var(--border);
  border-radius: var(--radius-full);
}

.lf-steps__fill {
  display: block;
  height: 100%;
  background: var(--primary);
  border-radius: var(--radius-full);
  transition: width var(--transition);
}

@media (max-width: 640px) {
  .lf-card__facts {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* A format we recognise but do not import yet — said plainly, on the chip. */
.lf-attach__warn {
  color: var(--warning);
}

/* ==========================================================================
   The interview

   One card that asks, shows what it already knows, and confirms once.
   ========================================================================== */

.lf-answers {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0.5rem 0.625rem;
  list-style: none;
  background: var(--secondary);
  border-radius: var(--radius-lg);
}

.lf-answers li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
}

.lf-answers__label {
  min-width: 5rem;
  color: var(--muted-foreground);
}

.lf-answers__value {
  flex: 1 1;
  font-weight: 600;
  color: var(--foreground);
}

.lf-answers__from {
  font-size: 0.6875rem;
  font-style: normal;
  color: var(--muted-foreground);
}

.lf-answers__edit {
  padding: 0.125rem 0.4375rem;
  font-size: 0.6875rem;
  color: var(--primary);
  background: transparent;
  border: 1px solid var(--primary-soft);
  border-radius: var(--radius-full);
  cursor: pointer;
}

.lf-answers__edit:hover {
  background: var(--primary-soft);
}

.lf-ask {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.lf-ask__prompt {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--foreground);
}

/* The only place in the flow that quotes a cost — because it is the only
   answer that commits the user to GPU time. */
.lf-ask__cost {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  padding: 0.375rem 0.5rem;
  font-size: 0.75rem;
  color: var(--warning);
  background: var(--warning-soft);
  border-radius: var(--radius);
}

.lf-ask__options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.lf-ask__option {
  padding: 0.4375rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--foreground);
  background: var(--background);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: border-color var(--transition-fast), background var(--transition-fast);
}

.lf-ask__option:hover {
  color: var(--primary);
  background: var(--primary-soft);
  border-color: var(--primary);
}

.lf-review,
.lf-confirm {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.lf-review__coverage {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--success);
}

.lf-review__coverage--warn {
  color: var(--warning);
}

.lf-confirm__lines {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin: 0;
  padding: 0.75rem;
  list-style: none;
  font-size: 0.8125rem;
  color: var(--foreground);
  background: var(--secondary);
  border-radius: var(--radius-lg);
}

.lf-confirm__lines i {
  width: 1rem;
  color: var(--primary);
}

.lf-confirm__names {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.lf-confirm__names label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.6875rem;
  color: var(--muted-foreground);
}

.lf-confirm__names .lf-card__rename {
  width: 12rem;
}

/* ==========================================================================
   The busy strip

   Named, measured, alive and escapable — the four things the interview card
   was missing while it worked, which is why a working import read as a frozen
   one and sent the user to the Change button.
   ========================================================================== */

.lf-busy {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--secondary);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.lf-busy__line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--foreground);
}

.lf-busy__spin {
  flex-shrink: 0;
  color: var(--primary);
}

.lf-busy__label {
  flex: 1 1 auto;
  min-width: 0;
}

/* Tabular figures so a climbing percentage does not jiggle its neighbours. */
.lf-busy__percent,
.lf-busy__elapsed {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  font-size: 0.75rem;
}

.lf-busy__percent {
  color: var(--primary);
  font-weight: 600;
}

.lf-busy__elapsed {
  color: var(--muted-foreground);
}

.lf-busy__detail {
  margin: 0;
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.lf-busy__track {
  position: relative;
  height: 4px;
  overflow: hidden;
  background: var(--border);
  border-radius: var(--radius-full);
}

.lf-busy__fill {
  display: block;
  height: 100%;
  background: var(--primary);
  border-radius: var(--radius-full);
  transition: width var(--transition);
}

/*
 * No real percentage exists for this step, so the bar sweeps instead of
 * filling. Deliberately NOT a fabricated number: a bar that parks at 90%
 * promises an ending it cannot deliver, and teaches the user to distrust every
 * bar in the product.
 */
.lf-busy__track--indeterminate .lf-busy__fill {
  position: absolute;
  width: 35%;
  animation: lf-busy-sweep 1.4s ease-in-out infinite;
}

@keyframes lf-busy-sweep {
  from { left: -35%; }
  to { left: 100%; }
}

.lf-busy__stop {
  align-self: flex-start;
  min-height: 2.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  color: var(--foreground);
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  transition: border-color var(--transition), color var(--transition);
}

.lf-busy__stop:hover {
  color: var(--destructive);
  border-color: var(--destructive);
}

/* The elapsed counter is the one live signal a stalled tab cannot fake, so it
   stays; only the sweep is motion worth suppressing. */
@media (prefers-reduced-motion: reduce) {
  .lf-busy__track--indeterminate .lf-busy__fill {
    animation: none;
    left: 0;
    width: 100%;
    opacity: 0.45;
  }
}

/* A busy card is visibly a different state, not the same card with a spinner. */
.lf-card--busy {
  border-color: var(--primary);
}

/*
 * Disabled controls on a card.
 *
 * There was no :disabled rule anywhere in this file, so every \`disabled\`
 * button rendered at full strength with a normal cursor — it looked pressable,
 * did nothing when pressed, and gave no reason. That is worse than no button.
 */
.lf-answers__edit:disabled,
.lf-card__run:disabled,
.lf-card__link:disabled,
.lf-card__mini:disabled,
.lf-busy__stop:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: auto; /* keep the title tooltip reachable */
}

.lf-answers__edit:disabled:hover,
.lf-card__run:disabled:hover,
.lf-card__link:disabled:hover,
.lf-card__mini:disabled:hover {
  filter: none;
  border-color: var(--border);
}

/* A run that created nothing reads as a failure, not as a near-miss. */
.lf-card__result--bad {
  color: var(--destructive);
}

.lf-card__hint--reason {
  color: var(--muted-foreground);
  font-style: italic;
}
`, "",{"version":3,"sources":["webpack://./src/pages/assistant/styles/assistant.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;;;EAgBE;;AAEF;EACE,iDAAiD;EACjD,qBAAqB;EACrB,qBAAqB;EACrB;0DACwD;EACxD,mBAAmB;;EAEnB,kBAAkB;EAClB,aAAa;EACb,oBAAoB;EACpB,MAAM;EACN;;kEAEgE;EAChE,iBAAiB;EACjB,gBAAgB;EAChB,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;;+EAE+E;;AAE/E;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,YAAY;EACZ,qCAAqC;EACrC,iCAAiC;EACjC,iEAAiE;EACjE,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,0BAA0B;EAC1B,aAAa;AACf;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,gBAAgB;EAChB,sCAAsC;AACxC;;AAEA;EACE,SAAO;EACP,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,mBAAmB;EACnB,kBAAkB;EAClB,kBAAkB;EAClB,gBAAgB;EAChB,gCAAgC;EAChC,0BAA0B;EAC1B,YAAY;EACZ,4BAA4B;EAC5B,eAAe;EACf,6CAA6C;AAC/C;;AAEA;EACE,gCAAgC;AAClC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,eAAe;EACf,8BAA8B;EAC9B,uBAAuB;EACvB,+BAA+B;EAC/B,4BAA4B;EAC5B,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,+BAA+B;EAC/B,mBAAmB;EACnB,uBAAuB;EACvB,8BAA8B;EAC9B,4BAA4B;EAC5B,kFAAkF;AACpF;;AAEA;EACE,4BAA4B;EAC5B,iCAAiC;AACnC;;AAEA;EACE,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;;;;;;;;;;EAUE;AACF;EACE,SAAO;EACP,YAAY;EACZ,gBAAgB;EAChB,oBAAoB;EACpB,wBAAwB;AAC1B;;AAEA,yDAAyD;AACzD;EACE,aAAa;AACf;;AAEA;EACE,oBAAoB;EACpB,gBAAgB;EAChB,iBAAiB;EACjB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,eAAe;AACjB;;AAEA;EACE,SAAO;EACP,aAAa;EACb,gBAAgB;EAChB,2BAA2B;AAC7B;;AAEA;EACE,sBAAsB;EACtB,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,mBAAmB;AACrB;;AAEA;EACE,mBAAmB;EACnB,iBAAiB;EACjB,oBAAoB;EACpB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,8BAA8B;AAChC;;AAEA;EACE,SAAS;EACT,UAAU;EACV,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,+BAA+B;EAC/B,6CAA6C;AAC/C;;AAEA;EACE,6BAA6B;AAC/B;;AAEA;EACE,+BAA+B;AACjC;;AAEA;EACE,SAAO;EACP,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,YAAY;EACZ,eAAe;EACf,gBAAgB;EAChB,uBAAuB;EACvB,YAAY;EACZ,eAAe;AACjB;;AAEA;EACE,cAAc;EACd,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,cAAc;EACd,YAAY;AACd;;AAEA;EACE,gBAAgB;EAChB,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,cAAc;EACd,aAAa;EACb,sBAAsB;EACtB,UAAU;EACV,0CAA0C;AAC5C;;AAEA;;EAEE,UAAU;AACZ;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,gBAAgB;EAChB,oBAAoB;EACpB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,+BAA+B;EAC/B,eAAe;EACf,2EAA2E;AAC7E;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;AAC9B;;AAEA;EACE,yBAAyB;EACzB,mCAAmC;AACrC;;AAEA;;;;;;;;EAQE;AACF;EACE,SAAO;EACP,YAAY;EACZ,sBAAsB;EACtB,wBAAwB;EACxB,oBAAoB;EACpB,wBAAwB;EACxB,uBAAuB;EACvB,8BAA8B;EAC9B,+BAA+B;AACjC;;AAEA;EACE,cAAc;EACd,yBAAyB;EACzB,mCAAmC;AACrC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,WAAW;EACX,yBAAyB;EACzB,kBAAkB;EAClB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,+BAA+B;EAC/B,eAAe;EACf,2EAA2E;AAC7E;;AAEA;EACE,yBAAyB;EACzB,mCAAmC;AACrC;;AAEA;;+EAE+E;;AAE/E;EACE,SAAO;EACP,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,aAAa;EACb,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;EACd,aAAa;EACb,4CAA4C;EAC5C,sCAAsC;AACxC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,WAAW;EACX,YAAY;EACZ,oBAAoB;EACpB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,+BAA+B;EAC/B,eAAe;EACf,2EAA2E;AAC7E;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;AAC9B;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,qBAAqB;EACrB,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,YAAY;AACd;;AAEA;EACE,SAAS;EACT,gBAAgB;EAChB,gDAAgD;EAChD,gBAAgB;EAChB,uBAAuB;EACvB,wBAAwB;EACxB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,SAAO;AACT;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,QAAQ;EACR,WAAW;EACX,aAAa;EACb,sBAAsB;EACtB,gBAAgB;EAChB,gBAAgB;EAChB,0BAA0B;EAC1B,+BAA+B;EAC/B,4BAA4B;EAC5B,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,wBAAwB;EACxB,oBAAoB;EACpB,gCAAgC;EAChC,gBAAgB;EAChB,uBAAuB;EACvB,YAAY;EACZ,+BAA+B;EAC/B,eAAe;AACjB;;AAEA;EACE,4BAA4B;AAC9B;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,cAAc;EACd,cAAc;EACd,gBAAgB;EAChB,kBAAkB;EAClB,oBAAoB;EACpB,gBAAgB;EAChB,kCAAkC;EAClC,4BAA4B;EAC5B,+BAA+B;EAC/B,4BAA4B;EAC5B,eAAe;EACf,kFAAkF;AACpF;;AAEA;EACE,kCAAkC;EAClC,kCAAkC;AACpC;;AAEA,+EAA+E;;AAE/E;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;EACd,aAAa;EACb,2CAA2C;EAC3C,oBAAoB;EACpB,yBAAyB;EACzB,mCAAmC;EACnC,sCAAsC;AACxC;;AAEA;EACE,SAAO;EACP,YAAY;AACd;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,cAAc;EACd,aAAa;EACb,uBAAuB;EACvB,kBAAkB;EAClB,gBAAgB;EAChB,yBAAyB;EACzB,uBAAuB;EACvB,8BAA8B;EAC9B,+BAA+B;EAC/B,eAAe;AACjB;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,oCAAoC;AACtC;;AAEA,8EAA8E;;AAE9E;EACE,SAAO;EACP,aAAa;EACb,aAAa;EACb,sBAAsB;AACxB;;AAEA;EACE,SAAO;EACP,aAAa;EACb,gBAAgB;AAClB;;AAEA;;+EAE+E;;AAE/E;EACE,4BAA4B;EAC5B,cAAc;EACd,kEAAkE;AACpE;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,eAAe;EACf,uBAAuB;EACvB,mBAAmB;EACnB,qBAAqB;EACrB,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA;EACE,kBAAkB;EAClB,gDAAgD;EAChD,gBAAgB;EAChB,iBAAiB;EACjB,uBAAuB;EACvB,wBAAwB;AAC1B;;AAEA;EACE,gBAAgB;EAChB,cAAc;EACd,uDAAuD;EACvD,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,aAAa;EACb,gBAAgB;EAChB,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,8BAA8B;AAChC;;AAEA;sCACsC;AACtC;EACE,aAAa;EACb,uEAAuE;EACvE,aAAa;EACb,qCAAqC;AACvC;;AAEA;EACE,kBAAkB;EAClB,aAAa;EACb,uBAAuB;EACvB,YAAY;EACZ,iBAAiB;EACjB,gBAAgB;EAChB,uBAAuB;EACvB,+BAA+B;EAC/B,4BAA4B;EAC5B,eAAe;EACf;qCACmC;AACrC;;AAEA;EACE,6BAA6B;EAC7B,kCAAkC;EAClC,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,4BAA4B;EAC5B,iCAAiC;AACnC;;AAEA;EACE,iCAAiC;EACjC,+BAA+B;AACjC;;AAEA;EACE,4BAA4B;AAC9B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,eAAe;EACf,gBAAgB;EAChB,oBAAoB;EACpB,qBAAqB;EACrB,yBAAyB;EACzB,+BAA+B;AACjC;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,cAAc;EACd,YAAY;AACd;;AAEA;EACE,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;AAC1B;;AAEA;EACE,yEAAyE;EACzE,oBAAoB;EACpB,qBAAqB;EACrB,4BAA4B;EAC5B,gBAAgB;EAChB,kBAAkB;EAClB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,aAAa;EACb,2BAA2B;EAC3B,mBAAmB;EACnB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,qBAAqB;EACrB,uBAAuB;EACvB,iCAAiC;AACnC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,aAAa;EACb,qCAAqC;EACrC,iBAAiB;EACjB,mCAAmC;AACrC;;AAEA;EACE,qBAAqB;EACrB,oBAAoB;EACpB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,8BAA8B;AAChC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,2BAA2B;EAC3B,kBAAkB;EAClB,8BAA8B;EAC9B,uBAAuB;EACvB,+BAA+B;EAC/B,iCAAiC;EACjC,eAAe;EACf;qCACmC;AACrC;;AAEA;EACE,qBAAqB;EACrB,yBAAyB;EACzB,4BAA4B;AAC9B;;AAEA;;+EAE+E;;AAE/E;EACE,kBAAkB;EAClB,SAAO;EACP,aAAa;EACb,aAAa;AACf;;AAEA;EACE,SAAO;EACP,aAAa;EACb,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;EACX,4BAA4B;EAC5B,cAAc;EACd,iEAAiE;AACnE;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,eAAe;EACf,UAAU;EACV,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,wBAAwB;EACxB,0BAA0B;EAC1B,+BAA+B;EAC/B,iCAAiC;EACjC,4BAA4B;EAC5B,eAAe;EACf,6CAA6C;AAC/C;;AAEA;EACE,4BAA4B;AAC9B;;AAEA,8EAA8E;;AAE9E;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;EACX;;yEAEuE;EACvE,yDAAyD;AAC3D;;AAEA;EACE;IACE,UAAU;IACV,0BAA0B;EAC5B;EACA;IACE,UAAU;IACV,eAAe;EACjB;AACF;;AAEA,0BAA0B;;AAE1B;EACE,qBAAqB;AACvB;;AAEA;EACE,0BAA0B;EAC1B,0BAA0B;EAC1B,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,qBAAqB;EACrB,uBAAuB;EACvB,4BAA4B;EAC5B,+BAA+B;EAC/B,kFAAkF;AACpF;;AAEA,+BAA+B;;AAE/B;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;AACb;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,gBAAgB;EAChB,oBAAoB;EACpB,qBAAqB;EACrB,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,8BAA8B;AAChC;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,uBAAuB;AACzB;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,mBAAmB;AACrB;;AAEA;4CAC4C;AAC5C;EACE,WAAW;EACX,qBAAqB;EACrB,aAAa;EACb,cAAc;EACd,qBAAqB;EACrB,2BAA2B;EAC3B,0BAA0B;EAC1B,kBAAkB;EAClB,+CAA+C;AACjD;;AAEA;EACE;IACE,UAAU;EACZ;AACF;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,WAAW;EACX,SAAS;EACT,yBAAyB;EACzB,mBAAmB;EACnB,mCAAmC;EACnC,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,WAAW;EACX,SAAS;EACT,wBAAwB;EACxB,kBAAkB;EAClB,qBAAqB;EACrB,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA,2BAA2B;;AAE3B;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;EACd,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,iBAAiB;EACjB,0BAA0B;EAC1B,kBAAkB;EAClB,mDAAmD;AACrD;;AAEA;EACE,uBAAuB;AACzB;AACA;EACE,uBAAuB;AACzB;;AAEA;EACE,qBAAqB;EACrB,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE;;;IAGE,aAAa;IACb,qBAAqB;EACvB;EACA;IACE,UAAU;IACV,mBAAmB;EACrB;AACF;;AAEA,8BAA8B;;AAE9B;EACE,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ;uEACqE;EACrE,UAAU;EACV,0CAA0C;AAC5C;;AAEA;;EAEE,UAAU;AACZ;;AAEA;EACE;IACE,UAAU;EACZ;AACF;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,uBAAuB;EACvB,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;EAC9B,uBAAuB;EACvB,6BAA6B;EAC7B,+BAA+B;EAC/B,eAAe;EACf;uCACqC;AACvC;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;EAC5B,2BAA2B;AAC7B;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA,sBAAsB;;AAEtB;EACE,oBAAoB;AACtB;;AAEA;;;;;;;EAOE;AACF;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;EACX,eAAe;EACf,uBAAuB;EACvB,8BAA8B;EAC9B,4BAA4B;EAC5B,kFAAkF;AACpF;;AAEA;EACE,4BAA4B;EAC5B,iCAAiC;AACnC;;AAEA;EACE,WAAW;EACX,oBAAoB;EACpB,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,WAAW;AACb;;AAEA;;EAEE,yBAAyB;EACzB,kBAAkB;EAClB,gBAAgB;EAChB,+BAA+B;EAC/B,eAAe;EACf,6CAA6C;AAC/C;;AAEA;EACE,8BAA8B;EAC9B,uBAAuB;EACvB,+BAA+B;AACjC;;AAEA;EACE,4BAA4B;AAC9B;;AAEA;EACE,gCAAgC;EAChC,0BAA0B;EAC1B,YAAY;AACd;;AAEA;EACE,gCAAgC;AAClC;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;;+EAE+E;;AAE/E;EACE,qBAAqB;EACrB,gBAAgB;EAChB,qBAAqB;EACrB,4CAA4C;EAC5C,6EAA6E;AAC/E;;AAEA;EACE,2BAA2B;EAC3B,mCAAmC;AACrC;;AAEA;EACE,sBAAsB;EACtB,iBAAiB;EACjB,YAAY;AACd;;AAEA;EACE,wBAAwB;EACxB,gBAAgB;EAChB,uBAAuB;EACvB,wBAAwB;AAC1B;;AAEA;EACE,oBAAoB;AACtB;AACA;EACE,eAAe;AACjB;AACA;EACE,oBAAoB;AACtB;AACA;EACE,mBAAmB;AACrB;;AAEA;EACE,mBAAmB;EACnB,qBAAqB;AACvB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,mBAAmB;EACnB,wBAAwB;EACxB,8BAA8B;EAC9B,2CAA2C;EAC3C,iCAAiC;EACjC,oDAAoD;AACtD;;AAEA;EACE,iBAAiB;EACjB,YAAY;EACZ,mCAAmC;AACrC;;AAEA;EACE,4BAA4B;EAC5B,6BAA6B;EAC7B,kBAAkB;EAClB,+BAA+B;EAC/B,yBAAyB;EACzB,+BAA+B;AACjC;;AAEA;EACE,kBAAkB;EAClB,oBAAoB;AACtB;;AAEA;EACE,SAAS;EACT,sBAAsB;EACtB;kCACgC;EAChC,gBAAgB;EAChB,6BAA6B;EAC7B,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,iCAAiC;EACjC,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,oBAAoB;EACpB,kBAAkB;EAClB,cAAc;EACd,gBAAgB;EAChB,UAAU;AACZ;;AAEA;EACE,kBAAkB;EAClB,aAAa;EACb,eAAe;EACf,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,eAAe;EACf,oBAAoB;EACpB,8BAA8B;EAC9B,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,eAAe;EACf,UAAU;EACV,wEAAwE;AAC1E;;AAEA;;EAEE,UAAU;AACZ;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE;IACE,UAAU;EACZ;AACF;;AAEA;;+EAE+E;;AAE/E;EACE,cAAc;EACd,0CAA0C;EAC1C,yDAAyD;EACzD,mCAAmC;EACnC,uBAAuB;AACzB;;AAEA;EACE,4BAA4B;EAC5B,cAAc;AAChB;;AAEA;;;EAGE;AACF;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,gCAAgC;EAChC,uBAAuB;EACvB,8BAA8B;EAC9B,+BAA+B;EAC/B,4BAA4B;EAC5B,kFAAkF;AACpF;;AAEA;EACE,4BAA4B;EAC5B,iCAAiC;AACnC;;AAEA;;EAEE,gCAAgC;EAChC,gBAAgB;AAClB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA,yEAAyE;AACzE;EACE,aAAa;AACf;;AAEA;;;;EAIE;AACF;EACE,aAAa;EACb,mBAAmB;EACnB,aAAa;EACb,gBAAgB;AAClB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,iCAAiC;EACjC,eAAe;EACf,2EAA2E;AAC7E;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;AAC9B;;AAEA;EACE,qBAAqB;EACrB,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,mBAAmB;AACrB;;AAEA;;;EAGE;AACF;EACE,aAAa;EACb,eAAe;EACf,aAAa;EACb,qBAAqB;AACvB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,4BAA4B;EAC5B,kBAAkB;EAClB,gBAAgB;EAChB,wBAAwB;EACxB,uBAAuB;EACvB,+BAA+B;EAC/B,iCAAiC;EACjC,eAAe;EACf;gCAC8B;AAChC;;AAEA;EACE,+BAA+B;EAC/B,4BAA4B;EAC5B,qBAAqB;AACvB;;AAEA;EACE,oBAAoB;EACpB,qBAAqB;AACvB;;AAEA,sCAAsC;;AAEtC;;;;EAIE;AACF;EACE,kBAAkB,EAAE,kDAAkD;EACtE,aAAa;EACb,uBAAuB;EACvB,aAAa;EACb,sCAAsC;EACtC,4BAA4B;EAC5B,+BAA+B;EAC/B,+BAA+B;EAC/B,gBAAgB,EAAE,iDAAiD;AACrE;;AAEA;;;;;;;;EAQE;AACF;EACE,kBAAkB;EAClB,OAAO;EACP,QAAQ;EACR,SAAS;EACT,WAAW;EACX,yBAAyB;EACzB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,MAAM;EACN,SAAS;EACT,UAAU;EACV,0BAA0B;EAC1B,oDAAoD;AACtD;;AAEA;EACE,OAAO,UAAU,EAAE;EACnB,KAAK,UAAU,EAAE;AACnB;;AAEA,2EAA2E;AAC3E;EACE;IACE,eAAe;IACf,OAAO;IACP,WAAW;IACX,YAAY;EACd;AACF;;AAEA;EACE,mCAAmC;EACnC,gCAAgC;AAClC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,cAAc;EACd,eAAe;EACf,mBAAmB;EACnB,qBAAqB;EACrB,uBAAuB;EACvB,4BAA4B;AAC9B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,aAAa;EACb,YAAY;EACZ,SAAO;AACT;;AAEA;EACE,gBAAgB;EAChB,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;;;;;EAKE;AACF;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,aAAa;EACb,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,gBAAgB;EAChB,sBAAsB;EACtB,2BAA2B;AAC7B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA,2EAA2E;AAC3E;EACE,4BAA4B;EAC5B,gBAAgB;EAChB,qBAAqB;EACrB,+BAA+B;EAC/B,iCAAiC;AACnC;;AAEA;EACE,cAAc;EACd,uBAAuB;EACvB,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;EAC9B,uBAAuB;EACvB,+BAA+B;EAC/B,iCAAiC;EACjC,eAAe;AACjB;;AAEA;EACE,wBAAwB;EACxB,kCAAkC;AACpC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,cAAc;EACd,eAAe;EACf,kBAAkB;EAClB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,iCAAiC;EACjC,eAAe;AACjB;;AAEA;EACE,yBAAyB;EACzB,mCAAmC;AACrC;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,0EAA0E;EAC1E,cAAc;EACd,mBAAmB;EACnB,gCAAgC;EAChC,oBAAoB;EACpB,oBAAoB;EACpB,iBAAiB;EACjB,wBAAwB;EACxB,YAAY;AACd;;AAEA;EACE,8BAA8B;AAChC;;AAEA;;;EAGE;AACF;;EAEE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,WAAW;EACX,YAAY;EACZ,oBAAoB;EACpB,YAAY;EACZ,iCAAiC;EACjC,eAAe;EACf;uEACqE;AACvE;;AAEA;EACE,gCAAgC;EAChC,0BAA0B;AAC5B;;AAEA;EACE,gCAAgC;EAChC,8BAA8B;AAChC;;AAEA;EACE,sBAAsB;AACxB;;AAEA;EACE,8BAA8B;EAC9B,4BAA4B;EAC5B,mBAAmB;AACrB;;AAEA;EACE,oCAAoC;EACpC,8BAA8B;AAChC;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,cAAc;EACd,YAAY;EACZ,YAAY;EACZ,mBAAmB;EACnB,kBAAkB;EAClB,gBAAgB;EAChB,8BAA8B;EAC9B,uBAAuB;EACvB,+BAA+B;EAC/B,iCAAiC;EACjC,eAAe;EACf;uCACqC;AACvC;;AAEA;EACE,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,wBAAwB;EACxB,kCAAkC;AACpC;;AAEA;EACE,qBAAqB;EACrB,+BAA+B;EAC/B,yBAAyB;AAC3B;;AAEA;EACE,qBAAqB;EACrB,4BAA4B;AAC9B;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,SAAO;EACP,kBAAkB;AACpB;;AAEA;EACE,cAAc;EACd,6BAA6B;EAC7B,oBAAoB;EACpB,kCAAkC;EAClC,8BAA8B;AAChC;;AAEA;EACE,gBAAgB;EAChB,yBAAyB;AAC3B;;AAEA;EACE,cAAc;EACd,oBAAoB;EACpB,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,iCAAiC;EACjC,eAAe;EACf,2EAA2E;AAC7E;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;AAC9B;;AAEA;;+EAE+E;;AAE/E;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,YAAY;EACZ,oCAAoC;EACpC,iCAAiC;EACjC,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,aAAa;AACf;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,WAAW;EACX,cAAc;EACd,gBAAgB;EAChB,sCAAsC;AACxC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;EACd,SAAS;EACT,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;AAC1B;;AAEA;EACE,kBAAkB;EAClB,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,aAAa;AACf;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,eAAe;EACf,oBAAoB;EACpB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,+BAA+B;EAC/B,eAAe;AACjB;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,WAAW;EACX,cAAc;EACd,SAAS;EACT,gBAAgB;EAChB,eAAe;EACf,sCAAsC;AACxC;;AAEA;EACE,cAAc;EACd,gBAAgB;EAChB,iBAAiB;EACjB,oBAAoB;EACpB,4BAA4B;AAC9B;;AAEA;EACE,cAAc;EACd,kBAAkB;EAClB,gBAAgB;EAChB,wBAAwB;AAC1B;;AAEA;EACE,cAAc;EACd,oBAAoB;EACpB,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,qCAAqC;EACrC,cAAc;EACd,cAAc;EACd,SAAS;EACT,gBAAgB;EAChB,sCAAsC;AACxC;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,cAAc;AAChB;;AAEA;EACE,mBAAmB;EACnB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,8BAA8B;AAChC;;AAEA;EACE,SAAS;EACT,6BAA6B;EAC7B,oBAAoB;EACpB,gBAAgB;EAChB,kCAAkC;EAClC,wBAAwB;AAC1B;;AAEA;EACE,gCAAgC;AAClC;;AAEA;EACE,SAAO;EACP,aAAa;EACb,gBAAgB;EAChB,eAAe;AACjB;;AAEA;EACE,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;EACE,mBAAmB;EACnB,kBAAkB;EAClB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,yBAAyB;EACzB,kBAAkB;EAClB,gBAAgB;EAChB,gCAAgC;EAChC,0BAA0B;EAC1B,YAAY;EACZ,+BAA+B;EAC/B,eAAe;AACjB;;AAEA;EACE,iBAAiB;EACjB,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA;EACE,oBAAoB;AACtB;;AAEA;EACE,aAAa;EACb,qBAAqB;EACrB,8BAA8B;EAC9B,aAAa;AACf;;AAEA;EACE,gBAAgB;EAChB,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,cAAc;EACd,6BAA6B;EAC7B,oBAAoB;EACpB,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,eAAe;EACf,YAAY;EACZ,oBAAoB;AACtB;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,oBAAoB;EACpB,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;EACd,qBAAqB;EACrB,oBAAoB;EACpB,qBAAqB;AACvB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,kBAAkB;EAClB,uBAAuB;EACvB,oBAAoB;EACpB,gBAAgB;EAChB,qBAAqB;EACrB,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,eAAe;EACf,kFAAkF;AACpF;;AAEA;EACE,yBAAyB;EACzB,4BAA4B;AAC9B;;AAEA;;+EAE+E;;AAE/E;EACE,eAAe;EACf,QAAQ;EACR,aAAa;EACb,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,aAAa;EACb,0BAA0B;EAC1B,kDAAkD;AACpD;;AAEA;EACE;IACE,UAAU;EACZ;EACA;IACE,UAAU;EACZ;AACF;;AAEA;EACE,WAAW;EACX,gBAAgB;EAChB,gBAAgB,EAAE,aAAa;EAC/B,iBAAiB;EACjB,gBAAgB;EAChB,0BAA0B;EAC1B,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,WAAW;EACX,sBAAsB;EACtB,sCAAsC;AACxC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,SAAS;EACT,oBAAoB;EACpB,gBAAgB;EAChB,gCAAgC;AAClC;;AAEA;EACE,oBAAoB;EACpB,8BAA8B;AAChC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,gBAAgB;EAChB,oBAAoB;EACpB,8BAA8B;EAC9B,uBAAuB;EACvB,YAAY;EACZ,+BAA+B;EAC/B,eAAe;AACjB;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;AAC9B;;AAEA;EACE,SAAS;EACT,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,yBAAyB;EACzB,+BAA+B;AACjC;;AAEA;EACE,4BAA4B;AAC9B;;AAEA;EACE,oBAAoB;EACpB,gCAAgC;AAClC;;AAEA;EACE,aAAa;EACb,cAAc;EACd,YAAY;AACd;;AAEA;EACE,iBAAiB;EACjB,0BAA0B;EAC1B,6BAA6B;EAC7B,oBAAoB;EACpB,kBAAkB;EAClB,wBAAwB;EACxB,iCAAiC;EACjC,+BAA+B;EAC/B,wBAAwB;EACxB,+BAA+B;AACjC;;AAEA;;+EAE+E;;AAE/E;;6CAE6C;AAC7C;EACE;IACE,qBAAqB;EACvB;AACF;;AAEA;EACE;IACE,kBAAkB;IAClB,iBAAiB;IACjB,WAAW;IACX,uBAAuB;IACvB,gBAAgB;IAChB,4BAA4B;EAC9B;AACF;;AAEA;2EAC2E;AAC3E;EACE;IACE,4BAA4B;EAC9B;;EAEA;IACE,kBAAkB;IAClB,iBAAiB;IACjB,WAAW;IACX,gBAAgB;IAChB,uBAAuB;IACvB,4BAA4B;IAC5B,uCAAuC;IACvC,4BAA4B;EAC9B;;EAEA;IACE,eAAe;EACjB;;EAEA;qBACmB;EACnB;IACE,gBAAgB;IAChB,uBAAuB;IACvB,iCAAiC;EACnC;;EAEA;IACE,WAAW;EACb;;EAEA;IACE,kBAAkB;IAClB,QAAQ;IACR,WAAW;IACX,0BAA0B;EAC5B;AACF;;AAEA;EACE;IACE,eAAe;EACjB;;EAEA;IACE,0BAA0B;EAC5B;AACF;;AAEA;;;;;+EAK+E;;AAE/E;EACE,kBAAkB;EAClB,SAAO;EACP,aAAa;EACb,aAAa;EACb,sBAAsB;AACxB;;AAEA;EACE,kBAAkB;EAClB,QAAQ;EACR,WAAW;EACX,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,aAAa;EACb,0BAA0B;EAC1B,0BAA0B;AAC5B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,WAAW;EACX,oBAAoB;EACpB,kBAAkB;EAClB,wBAAwB;EACxB,uBAAuB;EACvB,iCAAiC;EACjC,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,kBAAkB;EAClB,qBAAqB;AACvB;;AAEA;EACE,eAAe;AACjB;;AAEA;EACE,oBAAoB;EACpB,8BAA8B;AAChC;;AAEA;;;;;+EAK+E;;AAE/E;EACE,4BAA4B;EAC5B,sBAAsB;EACtB,WAAW;AACb;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,aAAa;EACb,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,aAAa;AACf;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,gCAAgC;EAChC,0BAA0B;EAC1B,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,SAAO;AACT;;AAEA;EACE,oBAAoB;EACpB,wBAAwB;AAC1B;;AAEA;EACE,gBAAgB;EAChB,kBAAkB;EAClB,8BAA8B;EAC9B,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,cAAc;EACd,yBAAyB;EACzB,oBAAoB;EACpB,gBAAgB;EAChB,qBAAqB;EACrB,+BAA+B;EAC/B,iCAAiC;AACnC;;AAEA;EACE,aAAa;EACb,0DAA0D;EAC1D,aAAa;EACb,SAAS;EACT,gBAAgB;EAChB,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,oBAAoB;EACpB,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,SAAS;EACT,gBAAgB;EAChB,mBAAmB;EACnB,gBAAgB;EAChB,wBAAwB;EACxB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,SAAS;EACT,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,eAAe;EACf,mBAAmB;EACnB,aAAa;AACf;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,yBAAyB;EACzB,kBAAkB;EAClB,wBAAwB;EACxB,4BAA4B;EAC5B,+BAA+B;EAC/B,iCAAiC;AACnC;;AAEA;EACE,6BAA6B;EAC7B,oBAAoB;EACpB,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;;;;;;EAME;AACF;EACE,WAAW;EACX,uBAAuB;EACvB,oBAAoB;EACpB,kBAAkB;EAClB,wBAAwB;EACxB,uBAAuB;EACvB,8BAA8B;EAC9B,+BAA+B;AACjC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,yBAAyB;EACzB,oBAAoB;EACpB,gBAAgB;EAChB,8BAA8B;EAC9B,uBAAuB;EACvB,uCAAuC;EACvC,iCAAiC;EACjC,eAAe;AACjB;;AAEA;EACE,qBAAqB;EACrB,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,SAAS;EACT,UAAU;EACV,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,aAAa;EACb,SAAS;EACT,wBAAwB;EACxB,kBAAkB;EAClB,yBAAyB;EACzB,mCAAmC;EACnC,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,aAAa;EACb,SAAS;EACT,wBAAwB;EACxB,oBAAoB;EACpB,gBAAgB;EAChB,qBAAqB;EACrB,4BAA4B;EAC5B,4BAA4B;AAC9B;;AAEA;EACE,qBAAqB;EACrB,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,eAAe;EACf,mBAAmB;EACnB,WAAW;AACb;;AAEA;;EAEE,oBAAoB;EACpB,mBAAmB;EACnB,cAAc;EACd,2BAA2B;EAC3B,oBAAoB;EACpB,gBAAgB;EAChB,YAAY;EACZ,4BAA4B;EAC5B,eAAe;EACf,6CAA6C;AAC/C;;AAEA;EACE,gCAAgC;EAChC,0BAA0B;AAC5B;;AAEA;EACE,gCAAgC;AAClC;;AAEA;EACE,oCAAoC;EACpC,8BAA8B;AAChC;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,yBAAyB;EACzB,oBAAoB;EACpB,gBAAgB;EAChB,qBAAqB;EACrB,uBAAuB;EACvB,YAAY;EACZ,eAAe;AACjB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA,6BAA6B;;AAE7B;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,SAAS;EACT,iBAAiB;EACjB,gBAAgB;EAChB,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,oCAAoC;EACpC,mBAAmB;EACnB,WAAW;EACX,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,kBAAkB;EAClB,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,6BAA6B;EAC7B,oBAAoB;AACtB;;AAEA;EACE,mBAAmB;EACnB,WAAW;EACX,gBAAgB;EAChB,yBAAyB;EACzB,iCAAiC;AACnC;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,0BAA0B;EAC1B,iCAAiC;EACjC,mCAAmC;AACrC;;AAEA;EACE;IACE,qCAAqC;EACvC;AACF;;AAEA,6EAA6E;AAC7E;EACE,qBAAqB;AACvB;;AAEA;;;;+EAI+E;;AAE/E;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,SAAS;EACT,wBAAwB;EACxB,gBAAgB;EAChB,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,kBAAkB;AACpB;;AAEA;EACE,eAAe;EACf,8BAA8B;AAChC;;AAEA;EACE,SAAO;EACP,gBAAgB;EAChB,wBAAwB;AAC1B;;AAEA;EACE,oBAAoB;EACpB,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,2BAA2B;EAC3B,oBAAoB;EACpB,qBAAqB;EACrB,uBAAuB;EACvB,qCAAqC;EACrC,iCAAiC;EACjC,eAAe;AACjB;;AAEA;EACE,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;AACb;;AAEA;EACE,SAAS;EACT,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;AAC1B;;AAEA;8CAC8C;AAC9C;EACE,aAAa;EACb,mBAAmB;EACnB,aAAa;EACb,SAAS;EACT,wBAAwB;EACxB,kBAAkB;EAClB,qBAAqB;EACrB,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,eAAe;EACf,aAAa;AACf;;AAEA;EACE,0BAA0B;EAC1B,oBAAoB;EACpB,gBAAgB;EAChB,wBAAwB;EACxB,6BAA6B;EAC7B,sCAAsC;EACtC,iCAAiC;EACjC,eAAe;EACf,kFAAkF;AACpF;;AAEA;EACE,qBAAqB;EACrB,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;;EAEE,aAAa;EACb,sBAAsB;EACtB,aAAa;AACf;;AAEA;EACE,SAAS;EACT,oBAAoB;EACpB,gBAAgB;EAChB,qBAAqB;AACvB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,aAAa;EACb,SAAS;EACT,gBAAgB;EAChB,gBAAgB;EAChB,oBAAoB;EACpB,wBAAwB;EACxB,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;EACE,WAAW;EACX,qBAAqB;AACvB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,YAAY;AACd;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,oBAAoB;EACpB,8BAA8B;AAChC;;AAEA;EACE,YAAY;AACd;;AAEA;;;;;;+EAM+E;;AAE/E;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;EACX,gBAAgB;EAChB,4BAA4B;EAC5B,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,oBAAoB;EACpB,wBAAwB;AAC1B;;AAEA;EACE,cAAc;EACd,qBAAqB;AACvB;;AAEA;EACE,cAAc;EACd,YAAY;AACd;;AAEA,6EAA6E;AAC7E;;EAEE,cAAc;EACd,kCAAkC;EAClC,kBAAkB;AACpB;;AAEA;EACE,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,SAAS;EACT,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,gBAAgB;EAChB,yBAAyB;EACzB,iCAAiC;AACnC;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,0BAA0B;EAC1B,iCAAiC;EACjC,mCAAmC;AACrC;;AAEA;;;;;EAKE;AACF;EACE,kBAAkB;EAClB,UAAU;EACV,kDAAkD;AACpD;;AAEA;EACE,OAAO,UAAU,EAAE;EACnB,KAAK,UAAU,EAAE;AACnB;;AAEA;EACE,sBAAsB;EACtB,mBAAmB;EACnB,yBAAyB;EACzB,kBAAkB;EAClB,wBAAwB;EACxB,6BAA6B;EAC7B,+BAA+B;EAC/B,4BAA4B;EAC5B,eAAe;EACf,mEAAmE;AACrE;;AAEA;EACE,yBAAyB;EACzB,gCAAgC;AAClC;;AAEA;uDACuD;AACvD;EACE;IACE,eAAe;IACf,OAAO;IACP,WAAW;IACX,aAAa;EACf;AACF;;AAEA,gFAAgF;AAChF;EACE,4BAA4B;AAC9B;;AAEA;;;;;;EAME;AACF;;;;;EAKE,aAAa;EACb,mBAAmB;EACnB,oBAAoB,EAAE,qCAAqC;AAC7D;;AAEA;;;;EAIE,YAAY;EACZ,2BAA2B;AAC7B;;AAEA,uEAAuE;AACvE;EACE,yBAAyB;AAC3B;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;AACpB","sourcesContent":["/**\n * AI Assistant — page layout and composition.\n *\n * Rules this file follows (see 0_web_labelflow/DESIGN_GUIDE.md):\n * - Every colour is a token. Both themes come free; nothing is hardcoded.\n * - Motion is 120-180ms and eased, never overshooting. The previous stylesheet\n *   used `cubic-bezier(0.34, 1.56, 0.64, 1)` on cards, hover and messages,\n *   which is the spring the guide's \"nothing bounces\" rule exists to forbid.\n * - Full-height regions use `100dvh` with a `100vh` line above as the fallback,\n *   so mobile browser chrome cannot clip the composer off the bottom.\n * - Type and spacing are `clamp()`ed rather than stepped through breakpoints;\n *   media queries are reserved for real reflow (the sidebar becoming a drawer).\n *\n * The 58px below is the header's `min-height` from styles/responsive.css. It is\n * subtracted along with the container gutter so the composer lands exactly on\n * the fold instead of hanging below it.\n */\n\n.lf-assistant {\n  --lf-gutter: clamp(0.75rem, 0.4rem + 2vw, 1.5rem);\n  --lf-sidebar-w: 17rem;\n  --lf-context-w: 21rem;\n  /* The reading measure for the conversation. Wider than a chat bubble because\n     assistant answers carry headings, bullets and code. */\n  --lf-measure: 46rem;\n\n  position: relative;\n  display: flex;\n  align-items: stretch;\n  gap: 0;\n  /* Height comes from the shell (.lf-fill-viewport in responsive.css). The\n     58px here was the header's assumed size, and it is 57px — close enough to\n     look right and still wrong by a pixel at every window size. */\n  min-height: 30rem;\n  overflow: hidden;\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  box-shadow: var(--shadow-sm);\n}\n\n/* ==========================================================================\n   Sidebar — conversation list\n   ========================================================================== */\n\n.lf-assistant__sidebar {\n  flex: 0 0 var(--lf-sidebar-w);\n  width: var(--lf-sidebar-w);\n  min-width: 0;\n  border-right: 1px solid var(--border);\n  background: var(--page-bg-subtle);\n  transition: flex-basis var(--transition), width var(--transition);\n  overflow: hidden;\n}\n\n.lf-assistant__sidebar.is-collapsed {\n  flex-basis: 0;\n  width: 0;\n  border-right-color: transparent;\n}\n\n.lf-sidebar {\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  width: var(--lf-sidebar-w);\n  min-height: 0;\n}\n\n.lf-sidebar__top {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  padding: 0.75rem;\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-sidebar__new {\n  flex: 1;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 0.5rem;\n  min-height: 2.25rem;\n  padding: 0 0.75rem;\n  font-size: 0.85rem;\n  font-weight: 600;\n  color: var(--primary-foreground);\n  background: var(--primary);\n  border: none;\n  border-radius: var(--radius);\n  cursor: pointer;\n  transition: background var(--transition-fast);\n}\n\n.lf-sidebar__new:hover {\n  background: var(--primary-hover);\n}\n\n.lf-sidebar__close {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 2.25rem;\n  height: 2.25rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  cursor: pointer;\n}\n\n.lf-sidebar__search {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  margin: 0.75rem 0.75rem 0.25rem;\n  padding: 0 0.625rem;\n  background: var(--card);\n  border: 1px solid var(--input);\n  border-radius: var(--radius);\n  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);\n}\n\n.lf-sidebar__search:focus-within {\n  border-color: var(--primary);\n  box-shadow: 0 0 0 3px var(--ring);\n}\n\n.lf-sidebar__search > i {\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n/*\n * The chrome belongs to the WRAPPER above, never to this input.\n *\n * theme-overrides.css styles every `input:focus` with `!important` — border,\n * 3px ring, background — so a page-level `border: none` here loses silently and\n * the input paints a second bordered box, with a second ring, INSIDE the\n * wrapper's. That is the doubled highlight this class exists to stop: the\n * markup carries `lf-field-bare`, which is that file's supported opt-out.\n * Measured on the live page before the fix: wrapper AND input both at\n * `1px rgb(16,185,129)` + `rgba(52,211,153,.4) 0 0 0 3px`.\n */\n.lf-sidebar__search input {\n  flex: 1;\n  min-width: 0;\n  height: 2.125rem;\n  font-size: 0.8125rem;\n  color: var(--foreground);\n}\n\n/* The UA's own clear affordance would sit beside ours. */\n.lf-sidebar__search input::-webkit-search-cancel-button {\n  display: none;\n}\n\n.lf-sidebar__search button {\n  display: inline-flex;\n  padding: 0.25rem;\n  font-size: 0.7rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  cursor: pointer;\n}\n\n.lf-sidebar__list {\n  flex: 1;\n  min-height: 0;\n  overflow-y: auto;\n  padding: 0.5rem 0.5rem 1rem;\n}\n\n.lf-sidebar__none {\n  margin: 1.5rem 0.75rem;\n  font-size: 0.8125rem;\n  line-height: 1.6;\n  color: var(--muted-foreground);\n}\n\n.lf-sidebar__group + .lf-sidebar__group {\n  margin-top: 0.75rem;\n}\n\n.lf-sidebar__group-title {\n  margin: 0 0 0.25rem;\n  padding: 0 0.5rem;\n  font-size: 0.6875rem;\n  font-weight: 600;\n  letter-spacing: 0.06em;\n  text-transform: uppercase;\n  color: var(--muted-foreground);\n}\n\n.lf-sidebar__threads {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n\n.lf-thread-row {\n  position: relative;\n  display: flex;\n  align-items: center;\n  border-radius: var(--radius-sm);\n  transition: background var(--transition-fast);\n}\n\n.lf-thread-row:hover {\n  background: var(--card-hover);\n}\n\n.lf-thread-row--active {\n  background: var(--primary-soft);\n}\n\n.lf-thread-row__main {\n  flex: 1;\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  min-width: 0;\n  padding: 0.5rem;\n  text-align: left;\n  background: transparent;\n  border: none;\n  cursor: pointer;\n}\n\n.lf-thread-row__icon {\n  flex-shrink: 0;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n.lf-thread-row--active .lf-thread-row__icon {\n  color: var(--primary);\n}\n\n.lf-thread-row__text {\n  display: flex;\n  flex-direction: column;\n  gap: 0.0625rem;\n  min-width: 0;\n}\n\n.lf-thread-row__title {\n  overflow: hidden;\n  font-size: 0.8125rem;\n  font-weight: 500;\n  color: var(--foreground);\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-thread-row__meta {\n  font-size: 0.6875rem;\n  color: var(--muted-foreground);\n}\n\n.lf-thread-row__tools {\n  display: flex;\n  flex-shrink: 0;\n  gap: 0.125rem;\n  padding-right: 0.25rem;\n  opacity: 0;\n  transition: opacity var(--transition-fast);\n}\n\n.lf-thread-row:hover .lf-thread-row__tools,\n.lf-thread-row:focus-within .lf-thread-row__tools {\n  opacity: 1;\n}\n\n.lf-thread-row__tool {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 1.625rem;\n  height: 1.625rem;\n  font-size: 0.6875rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n  transition: color var(--transition-fast), background var(--transition-fast);\n}\n\n.lf-thread-row__tool:hover {\n  color: var(--foreground);\n  background: var(--secondary);\n}\n\n.lf-thread-row__tool--danger:hover {\n  color: var(--destructive);\n  background: var(--destructive-soft);\n}\n\n/*\n * `width: 100%` PLUS horizontal margins overflowed the row by exactly those\n * margins, and the 3px focus ring the global layer adds pushed it further out\n * of a 17rem sidebar. As a flex child it fills the space it actually has.\n *\n * The border is neutral here, not `--primary`: this field was drawn in the\n * focus colour even while blurred, so a row being renamed looked focused when\n * focus had moved elsewhere. Focus chrome comes from the global layer.\n */\n.lf-thread-row__rename {\n  flex: 1;\n  min-width: 0;\n  margin: 0.25rem 0.5rem;\n  padding: 0.375rem 0.5rem;\n  font-size: 0.8125rem;\n  color: var(--foreground);\n  background: var(--card);\n  border: 1px solid var(--input);\n  border-radius: var(--radius-sm);\n}\n\n.lf-sidebar__foot {\n  flex-shrink: 0;\n  padding: 0.625rem 0.75rem;\n  border-top: 1px solid var(--border);\n}\n\n.lf-sidebar__clear {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.5rem;\n  width: 100%;\n  padding: 0.4375rem 0.5rem;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n  transition: color var(--transition-fast), background var(--transition-fast);\n}\n\n.lf-sidebar__clear:hover {\n  color: var(--destructive);\n  background: var(--destructive-soft);\n}\n\n/* ==========================================================================\n   Main column\n   ========================================================================== */\n\n.lf-assistant__main {\n  flex: 1;\n  display: flex;\n  flex-direction: column;\n  min-width: 0;\n  min-height: 0;\n  background: var(--card);\n}\n\n.lf-topbar {\n  display: flex;\n  align-items: center;\n  flex-shrink: 0;\n  gap: 0.375rem;\n  padding: 0.625rem clamp(0.625rem, 2vw, 1rem);\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-topbar__icon {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 2rem;\n  height: 2rem;\n  font-size: 0.8125rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n  transition: color var(--transition-fast), background var(--transition-fast);\n}\n\n.lf-topbar__icon:hover:not(:disabled) {\n  color: var(--foreground);\n  background: var(--secondary);\n}\n\n.lf-topbar__icon:disabled {\n  opacity: 0.4;\n  cursor: not-allowed;\n}\n\n.lf-topbar__icon--active {\n  color: var(--primary);\n  background: var(--primary-soft);\n}\n\n.lf-topbar__title {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  min-width: 0;\n}\n\n.lf-topbar__heading {\n  margin: 0;\n  overflow: hidden;\n  font-size: clamp(0.875rem, 0.8rem + 0.4vw, 1rem);\n  font-weight: 600;\n  letter-spacing: -0.01em;\n  color: var(--foreground);\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-topbar__spacer {\n  flex: 1;\n}\n\n.lf-topbar__menu {\n  position: relative;\n}\n\n.lf-topbar__dropdown {\n  position: absolute;\n  top: calc(100% + 0.375rem);\n  right: 0;\n  z-index: 20;\n  display: flex;\n  flex-direction: column;\n  min-width: 12rem;\n  padding: 0.25rem;\n  background: var(--popover);\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  box-shadow: var(--shadow-lg);\n}\n\n.lf-topbar__dropdown button {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  padding: 0.5rem 0.625rem;\n  font-size: 0.8125rem;\n  color: var(--popover-foreground);\n  text-align: left;\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n}\n\n.lf-topbar__dropdown button:hover {\n  background: var(--secondary);\n}\n\n.lf-topbar__dropdown button i {\n  width: 1rem;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n.lf-topbar__new {\n  display: inline-flex;\n  align-items: center;\n  flex-shrink: 0;\n  gap: 0.4375rem;\n  min-height: 2rem;\n  padding: 0 0.75rem;\n  font-size: 0.8125rem;\n  font-weight: 600;\n  color: var(--secondary-foreground);\n  background: var(--secondary);\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  cursor: pointer;\n  transition: background var(--transition-fast), border-color var(--transition-fast);\n}\n\n.lf-topbar__new:hover {\n  background: var(--secondary-hover);\n  border-color: var(--border-strong);\n}\n\n/* ---- error banner -------------------------------------------------------- */\n\n.lf-assistant__banner {\n  display: flex;\n  align-items: center;\n  flex-shrink: 0;\n  gap: 0.625rem;\n  padding: 0.625rem clamp(0.75rem, 2vw, 1rem);\n  font-size: 0.8125rem;\n  color: var(--destructive);\n  background: var(--destructive-soft);\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-assistant__banner > span {\n  flex: 1;\n  min-width: 0;\n}\n\n.lf-assistant__banner button {\n  display: inline-flex;\n  align-items: center;\n  flex-shrink: 0;\n  gap: 0.375rem;\n  padding: 0.25rem 0.5rem;\n  font-size: 0.75rem;\n  font-weight: 600;\n  color: var(--destructive);\n  background: transparent;\n  border: 1px solid currentColor;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n}\n\n.lf-assistant__banner button:disabled {\n  opacity: 0.5;\n  cursor: not-allowed;\n}\n\n.lf-assistant__banner-close {\n  border-color: transparent !important;\n}\n\n/* ---- body --------------------------------------------------------------- */\n\n.lf-assistant__body {\n  flex: 1;\n  min-height: 0;\n  display: flex;\n  flex-direction: column;\n}\n\n.lf-assistant__welcome-scroll {\n  flex: 1;\n  min-height: 0;\n  overflow-y: auto;\n}\n\n/* ==========================================================================\n   Welcome / empty state\n   ========================================================================== */\n\n.lf-welcome {\n  max-width: var(--lf-measure);\n  margin: 0 auto;\n  padding: clamp(1.5rem, 4vh, 3rem) clamp(1rem, 3vw, 1.75rem) 1.5rem;\n}\n\n.lf-welcome__intro {\n  text-align: center;\n}\n\n.lf-welcome__mark {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 2.75rem;\n  height: 2.75rem;\n  margin-bottom: 0.875rem;\n  font-size: 1.125rem;\n  color: var(--primary);\n  background: var(--primary-soft);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n}\n\n.lf-welcome__title {\n  margin: 0 0 0.5rem;\n  font-size: clamp(1.25rem, 1rem + 1.4vw, 1.75rem);\n  font-weight: 600;\n  line-height: 1.25;\n  letter-spacing: -0.02em;\n  color: var(--foreground);\n}\n\n.lf-welcome__subtitle {\n  max-width: 34rem;\n  margin: 0 auto;\n  font-size: clamp(0.8125rem, 0.78rem + 0.2vw, 0.9375rem);\n  line-height: 1.6;\n  color: var(--muted-foreground);\n}\n\n.lf-welcome__context {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: 0.375rem;\n  margin: 1rem 0 0;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n.lf-welcome__context i {\n  color: var(--info);\n}\n\n.lf-welcome__context--loading i {\n  color: var(--muted-foreground);\n}\n\n/* Suggestions: auto-fill so 320px gets one column and a wide screen gets two,\n   without a breakpoint deciding it. */\n.lf-welcome__suggestions {\n  display: grid;\n  grid-template-columns: repeat(auto-fill, minmax(min(18rem, 100%), 1fr));\n  gap: 0.625rem;\n  margin-top: clamp(1.25rem, 3vh, 2rem);\n}\n\n.lf-suggest {\n  position: relative;\n  display: flex;\n  align-items: flex-start;\n  gap: 0.75rem;\n  padding: 0.875rem;\n  text-align: left;\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  cursor: pointer;\n  transition: border-color var(--transition-fast), background var(--transition-fast),\n    box-shadow var(--transition-fast);\n}\n\n.lf-suggest:hover {\n  background: var(--card-hover);\n  border-color: var(--border-strong);\n  box-shadow: var(--shadow-sm);\n}\n\n.lf-suggest:focus-visible {\n  outline: none;\n  border-color: var(--primary);\n  box-shadow: 0 0 0 3px var(--ring);\n}\n\n.lf-suggest--context {\n  border-color: var(--primary-soft);\n  background: var(--primary-soft);\n}\n\n.lf-suggest--context:hover {\n  border-color: var(--primary);\n}\n\n.lf-suggest__icon {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 1.875rem;\n  height: 1.875rem;\n  font-size: 0.8125rem;\n  color: var(--primary);\n  background: var(--accent);\n  border-radius: var(--radius-sm);\n}\n\n.lf-suggest--context .lf-suggest__icon {\n  background: var(--card);\n}\n\n.lf-suggest__text {\n  display: flex;\n  flex-direction: column;\n  gap: 0.1875rem;\n  min-width: 0;\n}\n\n.lf-suggest__label {\n  font-size: 0.8125rem;\n  font-weight: 600;\n  color: var(--foreground);\n}\n\n.lf-suggest__prompt {\n  /* Two lines of the real question, so the card shows what will be sent. */\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n  overflow: hidden;\n  font-size: 0.75rem;\n  line-height: 1.5;\n  color: var(--muted-foreground);\n}\n\n.lf-suggest__tag {\n  position: absolute;\n  top: 0.5rem;\n  right: 0.5rem;\n  padding: 0.0625rem 0.375rem;\n  font-size: 0.625rem;\n  font-weight: 600;\n  letter-spacing: 0.03em;\n  text-transform: uppercase;\n  color: var(--primary);\n  background: var(--card);\n  border-radius: var(--radius-full);\n}\n\n.lf-welcome__links {\n  display: flex;\n  align-items: center;\n  flex-wrap: wrap;\n  gap: 0.375rem;\n  margin-top: clamp(1.25rem, 3vh, 2rem);\n  padding-top: 1rem;\n  border-top: 1px solid var(--border);\n}\n\n.lf-welcome__links-label {\n  margin-right: 0.25rem;\n  font-size: 0.6875rem;\n  font-weight: 600;\n  letter-spacing: 0.06em;\n  text-transform: uppercase;\n  color: var(--muted-foreground);\n}\n\n.lf-welcome__link {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  padding: 0.3125rem 0.625rem;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-full);\n  cursor: pointer;\n  transition: color var(--transition-fast), border-color var(--transition-fast),\n    background var(--transition-fast);\n}\n\n.lf-welcome__link:hover {\n  color: var(--primary);\n  background: var(--accent);\n  border-color: var(--primary);\n}\n\n/* ==========================================================================\n   Thread\n   ========================================================================== */\n\n.lf-thread {\n  position: relative;\n  flex: 1;\n  min-height: 0;\n  display: flex;\n}\n\n.lf-thread__scroll {\n  flex: 1;\n  min-height: 0;\n  overflow-y: auto;\n  overflow-x: hidden;\n}\n\n.lf-thread__inner {\n  display: flex;\n  flex-direction: column;\n  gap: 1.5rem;\n  max-width: var(--lf-measure);\n  margin: 0 auto;\n  padding: clamp(1rem, 3vh, 1.75rem) clamp(1rem, 3vw, 1.75rem) 1rem;\n}\n\n.lf-thread__jump {\n  position: absolute;\n  right: 1rem;\n  bottom: 0.75rem;\n  z-index: 5;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 2rem;\n  height: 2rem;\n  font-size: 0.75rem;\n  color: var(--foreground);\n  background: var(--popover);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-full);\n  box-shadow: var(--shadow-md);\n  cursor: pointer;\n  transition: background var(--transition-fast);\n}\n\n.lf-thread__jump:hover {\n  background: var(--secondary);\n}\n\n/* ---- messages ----------------------------------------------------------- */\n\n.lf-msg {\n  display: flex;\n  flex-direction: column;\n  gap: 0.5rem;\n  /* One short, non-overshooting entrance. The old sheet alternated two\n     different slide-ins keyed off :nth-child(odd/even), so the animation a\n     message got depended on its position and flipped as the list grew. */\n  animation: lf-msg-in var(--transition-slow) ease-out both;\n}\n\n@keyframes lf-msg-in {\n  from {\n    opacity: 0;\n    transform: translateY(6px);\n  }\n  to {\n    opacity: 1;\n    transform: none;\n  }\n}\n\n/* -- the user's turn -- */\n\n.lf-msg--user {\n  align-items: flex-end;\n}\n\n.lf-msg__bubble {\n  max-width: min(85%, 34rem);\n  padding: 0.625rem 0.875rem;\n  font-size: 0.9375rem;\n  line-height: 1.6;\n  color: var(--foreground);\n  white-space: pre-wrap;\n  overflow-wrap: anywhere;\n  background: var(--secondary);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg) var(--radius-lg) var(--radius-sm) var(--radius-lg);\n}\n\n/* -- the assistant's turn -- */\n\n.lf-msg__meta {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n}\n\n.lf-msg__mark {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 1.375rem;\n  height: 1.375rem;\n  font-size: 0.6875rem;\n  color: var(--primary);\n  background: var(--primary-soft);\n  border-radius: var(--radius-sm);\n}\n\n.lf-msg__author {\n  font-size: 0.75rem;\n  font-weight: 600;\n  letter-spacing: 0.01em;\n  color: var(--muted-foreground);\n}\n\n.lf-msg__badge {\n  font-size: 0.625rem !important;\n}\n\n.lf-msg__body {\n  font-size: 0.9375rem;\n  line-height: 1.7;\n  color: var(--foreground);\n  overflow-wrap: anywhere;\n}\n\n.lf-msg__body > :first-child {\n  margin-top: 0;\n}\n\n.lf-msg__body > :last-child {\n  margin-bottom: 0;\n}\n\n.lf-msg__body p {\n  margin: 0 0 0.75rem;\n}\n\n/* The streaming caret: a pseudo-element on the body, so no extra node can be\n   orphaned by a chunk landing mid-render. */\n.lf-msg__body--streaming::after {\n  content: '';\n  display: inline-block;\n  width: 0.5rem;\n  height: 1.05em;\n  margin-left: 0.125rem;\n  vertical-align: text-bottom;\n  background: var(--primary);\n  border-radius: 1px;\n  animation: lf-caret 1s steps(2, start) infinite;\n}\n\n@keyframes lf-caret {\n  50% {\n    opacity: 0;\n  }\n}\n\n.lf-msg--error .lf-msg__body {\n  color: var(--destructive);\n}\n\n.lf-msg__error-text {\n  display: flex;\n  align-items: flex-start;\n  gap: 0.5rem;\n  margin: 0;\n  padding: 0.625rem 0.75rem;\n  font-size: 0.875rem;\n  background: var(--destructive-soft);\n  border-radius: var(--radius);\n}\n\n.lf-msg__notice {\n  display: flex;\n  align-items: flex-start;\n  gap: 0.5rem;\n  margin: 0;\n  padding: 0.5rem 0.625rem;\n  font-size: 0.75rem;\n  color: var(--warning);\n  background: var(--warning-soft);\n  border-radius: var(--radius-sm);\n}\n\n/* -- typing indicator -- */\n\n.lf-msg__typing {\n  display: flex;\n  align-items: center;\n  gap: 0.3125rem;\n  padding: 0.25rem 0;\n}\n\n.lf-msg__typing span:not(.lf-msg__typing-text) {\n  width: 0.3125rem;\n  height: 0.3125rem;\n  background: var(--primary);\n  border-radius: 50%;\n  animation: lf-typing 1.2s ease-in-out infinite both;\n}\n\n.lf-msg__typing span:nth-child(1) {\n  animation-delay: -0.24s;\n}\n.lf-msg__typing span:nth-child(2) {\n  animation-delay: -0.12s;\n}\n\n.lf-msg__typing-text {\n  margin-left: 0.375rem;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n@keyframes lf-typing {\n  0%,\n  70%,\n  100% {\n    opacity: 0.35;\n    transform: scale(0.8);\n  }\n  35% {\n    opacity: 1;\n    transform: scale(1);\n  }\n}\n\n/* -- per-message actions -- */\n\n.lf-msg__actions {\n  display: flex;\n  align-items: center;\n  gap: 0.25rem;\n  /* Hidden until the message is hovered or focused, so the prose stays clean;\n     always visible on touch, where there is no hover to reveal them. */\n  opacity: 0;\n  transition: opacity var(--transition-fast);\n}\n\n.lf-msg:hover .lf-msg__actions,\n.lf-msg:focus-within .lf-msg__actions {\n  opacity: 1;\n}\n\n@media (hover: none) {\n  .lf-msg__actions {\n    opacity: 1;\n  }\n}\n\n.lf-msg__actions--user {\n  justify-content: flex-end;\n}\n\n.lf-msg__action {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  padding: 0.25rem 0.5rem;\n  font-size: 0.6875rem;\n  font-weight: 500;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: 1px solid transparent;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n  transition: color var(--transition-fast), background var(--transition-fast),\n    border-color var(--transition-fast);\n}\n\n.lf-msg__action:hover:not(:disabled) {\n  color: var(--foreground);\n  background: var(--secondary);\n  border-color: var(--border);\n}\n\n.lf-msg__action:disabled {\n  opacity: 0.4;\n  cursor: not-allowed;\n}\n\n/* -- inline edit -- */\n\n.lf-msg--editing {\n  align-items: stretch;\n}\n\n/*\n * One surface, one border — the same contract as the composer.\n *\n * This used to be a permanently primary-bordered card wrapping a textarea that\n * drew its OWN primary border and ring on focus: two concentric green boxes\n * with a ring between them. The container now owns the chrome and lights up on\n * `:focus-within`; the textarea is bare (`lf-field-bare` in the markup).\n */\n.lf-msg__edit {\n  display: flex;\n  flex-direction: column;\n  gap: 0.5rem;\n  padding: 0.5rem;\n  background: var(--card);\n  border: 1px solid var(--input);\n  border-radius: var(--radius);\n  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);\n}\n\n.lf-msg__edit:focus-within {\n  border-color: var(--primary);\n  box-shadow: 0 0 0 3px var(--ring);\n}\n\n.lf-msg__edit-input {\n  width: 100%;\n  font-family: inherit;\n  font-size: 0.9375rem;\n  line-height: 1.6;\n  color: var(--foreground);\n  padding: 0.25rem;\n  resize: vertical;\n}\n\n.lf-msg__edit-actions {\n  display: flex;\n  justify-content: flex-end;\n  gap: 0.5rem;\n}\n\n.lf-msg__edit-cancel,\n.lf-msg__edit-save {\n  padding: 0.375rem 0.75rem;\n  font-size: 0.75rem;\n  font-weight: 600;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n  transition: background var(--transition-fast);\n}\n\n.lf-msg__edit-cancel {\n  color: var(--muted-foreground);\n  background: transparent;\n  border: 1px solid var(--border);\n}\n\n.lf-msg__edit-cancel:hover {\n  background: var(--secondary);\n}\n\n.lf-msg__edit-save {\n  color: var(--primary-foreground);\n  background: var(--primary);\n  border: none;\n}\n\n.lf-msg__edit-save:hover:not(:disabled) {\n  background: var(--primary-hover);\n}\n\n.lf-msg__edit-save:disabled {\n  opacity: 0.5;\n  cursor: not-allowed;\n}\n\n/* ==========================================================================\n   Markdown inside a reply\n   ========================================================================== */\n\n.lf-md-link {\n  color: var(--primary);\n  font-weight: 500;\n  text-decoration: none;\n  border-bottom: 1px solid var(--primary-soft);\n  transition: color var(--transition-fast), border-color var(--transition-fast);\n}\n\n.lf-md-link:hover {\n  color: var(--primary-hover);\n  border-bottom-color: var(--primary);\n}\n\n.lf-md-link__chevron {\n  margin-left: 0.1875rem;\n  font-size: 0.75em;\n  opacity: 0.7;\n}\n\n.lf-md-h {\n  margin: 1.25rem 0 0.5rem;\n  font-weight: 600;\n  letter-spacing: -0.01em;\n  color: var(--foreground);\n}\n\n.lf-md-h--1 {\n  font-size: 1.0625rem;\n}\n.lf-md-h--2 {\n  font-size: 1rem;\n}\n.lf-md-h--3 {\n  font-size: 0.9375rem;\n}\n.lf-md-h--4 {\n  font-size: 0.875rem;\n}\n\n.lf-md-list {\n  margin: 0 0 0.75rem;\n  padding-left: 1.25rem;\n}\n\n.lf-md-list li {\n  margin-bottom: 0.3125rem;\n}\n\n.lf-md-list li::marker {\n  color: var(--muted-foreground);\n}\n\n.lf-md-quote {\n  margin: 0 0 0.75rem;\n  padding: 0.5rem 0.875rem;\n  color: var(--muted-foreground);\n  border-left: 2px solid var(--border-strong);\n  background: var(--page-bg-subtle);\n  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;\n}\n\n.lf-md-rule {\n  margin: 1.25rem 0;\n  border: none;\n  border-top: 1px solid var(--border);\n}\n\n.lf-md-inline-code {\n  padding: 0.0625rem 0.3125rem;\n  font-family: var(--font-mono);\n  font-size: 0.875em;\n  color: var(--accent-foreground);\n  background: var(--accent);\n  border-radius: var(--radius-sm);\n}\n\n.lf-md-code {\n  position: relative;\n  margin: 0 0 0.875rem;\n}\n\n.lf-md-code__pre {\n  margin: 0;\n  padding: 0.875rem 1rem;\n  /* A long line scrolls inside the block; the page itself must never scroll\n     sideways (DESIGN_GUIDE §6). */\n  overflow-x: auto;\n  font-family: var(--font-mono);\n  font-size: 0.8125rem;\n  line-height: 1.6;\n  color: var(--foreground);\n  background: var(--page-bg-subtle);\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n}\n\n.lf-md-code__pre code {\n  font-family: inherit;\n  font-size: inherit;\n  color: inherit;\n  background: none;\n  padding: 0;\n}\n\n.lf-md-code__copy {\n  position: absolute;\n  top: 0.375rem;\n  right: 0.375rem;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 1.75rem;\n  height: 1.75rem;\n  font-size: 0.6875rem;\n  color: var(--muted-foreground);\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n  opacity: 0;\n  transition: opacity var(--transition-fast), color var(--transition-fast);\n}\n\n.lf-md-code:hover .lf-md-code__copy,\n.lf-md-code__copy:focus-visible {\n  opacity: 1;\n}\n\n.lf-md-code__copy:hover {\n  color: var(--foreground);\n}\n\n@media (hover: none) {\n  .lf-md-code__copy {\n    opacity: 1;\n  }\n}\n\n/* ==========================================================================\n   Composer\n   ========================================================================== */\n\n.lf-assistant__composer {\n  flex-shrink: 0;\n  padding: 0.75rem clamp(1rem, 3vw, 1.75rem);\n  padding-bottom: max(0.75rem, env(safe-area-inset-bottom));\n  border-top: 1px solid var(--border);\n  background: var(--card);\n}\n\n.lf-composer {\n  max-width: var(--lf-measure);\n  margin: 0 auto;\n}\n\n/*\n * The surface owns the border and the focus ring, so an attached dataset and\n * the text box read as ONE control rather than two stacked boxes.\n */\n.lf-composer__surface {\n  display: flex;\n  flex-direction: column;\n  gap: 0.25rem;\n  padding: 0.5rem 0.5rem 0.4375rem;\n  background: var(--card);\n  border: 1px solid var(--input);\n  border-radius: var(--radius-xl);\n  box-shadow: var(--shadow-sm);\n  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);\n}\n\n.lf-composer__surface:focus-within {\n  border-color: var(--primary);\n  box-shadow: 0 0 0 3px var(--ring);\n}\n\n.lf-composer__surface--invalid,\n.lf-composer__surface--invalid:focus-within {\n  border-color: var(--destructive);\n  box-shadow: none;\n}\n\n.lf-composer__tray {\n  padding-bottom: 0.125rem;\n}\n\n/* The file input is driven by the paperclip button; it is never shown. */\n.lf-composer__file {\n  display: none;\n}\n\n/*\n * The control rail, under the text rather than beside it. Buttons flanking the\n * textarea squeezed a long question into a narrow channel; full-width text is\n * the point of this layout.\n */\n.lf-composer__rail {\n  display: flex;\n  align-items: center;\n  gap: 0.375rem;\n  min-height: 2rem;\n}\n\n.lf-composer__attach {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 2rem;\n  height: 2rem;\n  font-size: 0.875rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-full);\n  cursor: pointer;\n  transition: color var(--transition-fast), background var(--transition-fast);\n}\n\n.lf-composer__attach:hover:not(:disabled) {\n  color: var(--foreground);\n  background: var(--secondary);\n}\n\n.lf-composer__attach.is-on {\n  color: var(--primary);\n  background: var(--primary-soft);\n}\n\n.lf-composer__attach:disabled {\n  opacity: 0.45;\n  cursor: not-allowed;\n}\n\n/*\n * One-tap prompts, ABOVE the surface: they are things to ask before typing, and\n * under the input they made a third stacked band.\n */\n.lf-composer__seeds {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.375rem;\n  margin-bottom: 0.5rem;\n}\n\n.lf-composer__seed {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  padding: 0.3125rem 0.6875rem;\n  font-size: 0.75rem;\n  font-weight: 500;\n  color: var(--foreground);\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-full);\n  cursor: pointer;\n  transition: border-color var(--transition-fast), background var(--transition-fast),\n    color var(--transition-fast);\n}\n\n.lf-composer__seed:hover {\n  background: var(--primary-soft);\n  border-color: var(--primary);\n  color: var(--primary);\n}\n\n.lf-composer__seed i {\n  font-size: 0.6875rem;\n  color: var(--primary);\n}\n\n/* ---- the staged dataset chip ---- */\n\n/*\n * flex-start, not center: the metadata wraps to three or four lines at phone\n * width, and centring left the file icon and the Replace/close buttons floating\n * halfway down an otherwise top-aligned block.\n */\n.lf-attach {\n  position: relative; /* anchors .lf-attach__bar along the bottom edge */\n  display: flex;\n  align-items: flex-start;\n  gap: 0.625rem;\n  padding: 0.5rem 0.5rem 0.5rem 0.625rem;\n  background: var(--secondary);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  overflow: hidden; /* so the bar is clipped to the rounded corners */\n}\n\n/*\n * The reading bar.\n *\n * INDETERMINATE ON PURPOSE. Nothing in the read reports a real percentage --\n * JSZip.loadAsync exposes no progress callback -- and a fabricated percentage\n * that parks at 90% is a worse lie than no number at all. What this has to\n * communicate is only \"still working\", which is exactly the thing a static\n * spinner cannot: the named phase beside it carries the detail.\n */\n.lf-attach__bar {\n  position: absolute;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  height: 2px;\n  background: var(--border);\n  overflow: hidden;\n}\n\n.lf-attach__bar::after {\n  content: '';\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  width: 40%;\n  background: var(--primary);\n  animation: lf-attach-sweep 1.1s ease-in-out infinite;\n}\n\n@keyframes lf-attach-sweep {\n  from { left: -40%; }\n  to { left: 100%; }\n}\n\n/* A sweeping bar is motion; honour the user's setting and hold it still. */\n@media (prefers-reduced-motion: reduce) {\n  .lf-attach__bar::after {\n    animation: none;\n    left: 0;\n    width: 100%;\n    opacity: 0.5;\n  }\n}\n\n.lf-attach--bad {\n  background: var(--destructive-soft);\n  border-color: var(--destructive);\n}\n\n.lf-attach__icon {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 1.75rem;\n  height: 1.75rem;\n  font-size: 0.875rem;\n  color: var(--primary);\n  background: var(--card);\n  border-radius: var(--radius);\n}\n\n.lf-attach--bad .lf-attach__icon {\n  color: var(--destructive);\n}\n\n.lf-attach__body {\n  display: flex;\n  flex-direction: column;\n  gap: 0.125rem;\n  min-width: 0;\n  flex: 1;\n}\n\n.lf-attach__name {\n  overflow: hidden;\n  font-size: 0.8125rem;\n  font-weight: 600;\n  color: var(--foreground);\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n/*\n * Five metadata chunks ran together into one unreadable line, because the only\n * thing between them was a gap. A middot between plain items gives the row\n * rhythm; the layout pill is skipped, since a dot butted against a pill reads\n * as punctuation that lost its word.\n */\n.lf-attach__meta {\n  display: flex;\n  align-items: center;\n  flex-wrap: wrap;\n  gap: 0.375rem;\n  font-size: 0.6875rem;\n  line-height: 1.5;\n  color: var(--muted-foreground);\n}\n\n.lf-attach__meta > * + *:not(.lf-attach__tag)::before {\n  content: '\\00b7';\n  margin-right: 0.375rem;\n  color: var(--border-strong);\n}\n\n.lf-attach--bad .lf-attach__meta {\n  color: var(--destructive);\n}\n\n/* The one highlighted fact: whether the archive's layout was understood. */\n.lf-attach__tag {\n  padding: 0.0625rem 0.4375rem;\n  font-weight: 600;\n  color: var(--primary);\n  background: var(--primary-soft);\n  border-radius: var(--radius-full);\n}\n\n.lf-attach__action {\n  flex-shrink: 0;\n  padding: 0.25rem 0.5rem;\n  font-size: 0.6875rem;\n  font-weight: 500;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-full);\n  cursor: pointer;\n}\n\n.lf-attach__action:hover:not(:disabled) {\n  color: var(--foreground);\n  border-color: var(--border-strong);\n}\n\n.lf-attach__close {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 1.75rem;\n  height: 1.75rem;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-full);\n  cursor: pointer;\n}\n\n.lf-attach__close:hover:not(:disabled) {\n  color: var(--destructive);\n  background: var(--destructive-soft);\n}\n\n.lf-composer__input {\n  width: 100%;\n  min-width: 0;\n  /* Grown to fit by the component; this is the single-row starting point. */\n  height: 2.5rem;\n  max-height: 12.5rem;\n  padding: 0.5rem 0.375rem 0.25rem;\n  font-family: inherit;\n  font-size: 0.9375rem;\n  line-height: 1.55;\n  color: var(--foreground);\n  resize: none;\n}\n\n.lf-composer__input::placeholder {\n  color: var(--muted-foreground);\n}\n\n/*\n * Send is a filled circle, matching the pill language of the rail rather than\n * the rounded square it used to be inside an --radius-xl surface.\n */\n.lf-composer__send,\n.lf-composer__stop {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 2rem;\n  height: 2rem;\n  font-size: 0.8125rem;\n  border: none;\n  border-radius: var(--radius-full);\n  cursor: pointer;\n  transition: background var(--transition-fast), color var(--transition-fast),\n    box-shadow var(--transition-fast), transform var(--transition-fast);\n}\n\n.lf-composer__send {\n  color: var(--primary-foreground);\n  background: var(--primary);\n}\n\n.lf-composer__send:hover:not(:disabled) {\n  background: var(--primary-hover);\n  box-shadow: var(--shadow-glow);\n}\n\n.lf-composer__send:active:not(:disabled) {\n  transform: scale(0.94);\n}\n\n.lf-composer__send:disabled {\n  color: var(--muted-foreground);\n  background: var(--secondary);\n  cursor: not-allowed;\n}\n\n.lf-composer__stop {\n  color: var(--destructive-foreground);\n  background: var(--destructive);\n}\n\n.lf-composer__stop:hover {\n  background: var(--destructive-hover);\n}\n\n.lf-composer__chip {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  flex-shrink: 1;\n  min-width: 0;\n  height: 2rem;\n  padding: 0 0.625rem;\n  font-size: 0.75rem;\n  font-weight: 500;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-full);\n  cursor: pointer;\n  transition: color var(--transition-fast), background var(--transition-fast),\n    border-color var(--transition-fast);\n}\n\n.lf-composer__chip-text {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-composer__chip:hover:not(:disabled) {\n  color: var(--foreground);\n  border-color: var(--border-strong);\n}\n\n.lf-composer__chip--on {\n  color: var(--primary);\n  background: var(--primary-soft);\n  border-color: transparent;\n}\n\n.lf-composer__chip--on:hover:not(:disabled) {\n  color: var(--primary);\n  border-color: var(--primary);\n}\n\n.lf-composer__chip:disabled {\n  opacity: 0.5;\n  cursor: not-allowed;\n}\n\n.lf-composer__spacer {\n  flex: 1;\n  min-width: 0.25rem;\n}\n\n.lf-composer__count {\n  flex-shrink: 0;\n  font-family: var(--font-mono);\n  font-size: 0.6875rem;\n  font-variant-numeric: tabular-nums;\n  color: var(--muted-foreground);\n}\n\n.lf-composer__count--over {\n  font-weight: 600;\n  color: var(--destructive);\n}\n\n.lf-composer__hint {\n  flex-shrink: 0;\n  font-size: 0.6875rem;\n  color: var(--muted-foreground);\n  white-space: nowrap;\n}\n\n.lf-composer__shortcuts {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 2rem;\n  height: 2rem;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-full);\n  cursor: pointer;\n  transition: color var(--transition-fast), background var(--transition-fast);\n}\n\n.lf-composer__shortcuts:hover {\n  color: var(--foreground);\n  background: var(--secondary);\n}\n\n/* ==========================================================================\n   Workspace context panel\n   ========================================================================== */\n\n.lf-assistant__context {\n  flex: 0 0 var(--lf-context-w);\n  width: var(--lf-context-w);\n  min-width: 0;\n  border-left: 1px solid var(--border);\n  background: var(--page-bg-subtle);\n  overflow: hidden;\n}\n\n.lf-context {\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  min-height: 0;\n}\n\n.lf-context__head {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 0.5rem;\n  flex-shrink: 0;\n  padding: 0.75rem;\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-context__title {\n  display: flex;\n  align-items: center;\n  gap: 0.4375rem;\n  margin: 0;\n  font-size: 0.8125rem;\n  font-weight: 600;\n  color: var(--foreground);\n}\n\n.lf-context__title i {\n  font-size: 0.75rem;\n  color: var(--info);\n}\n\n.lf-context__head-tools {\n  display: flex;\n  gap: 0.125rem;\n}\n\n.lf-context__tool {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 1.75rem;\n  height: 1.75rem;\n  font-size: 0.6875rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n}\n\n.lf-context__tool:hover:not(:disabled) {\n  color: var(--foreground);\n  background: var(--secondary);\n}\n\n.lf-context__switch {\n  display: flex;\n  align-items: flex-start;\n  gap: 0.5rem;\n  flex-shrink: 0;\n  margin: 0;\n  padding: 0.75rem;\n  cursor: pointer;\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-context__switch input {\n  flex-shrink: 0;\n  width: 0.9375rem;\n  height: 0.9375rem;\n  margin-top: 0.125rem;\n  accent-color: var(--primary);\n}\n\n.lf-context__switch-label {\n  display: block;\n  font-size: 0.75rem;\n  font-weight: 600;\n  color: var(--foreground);\n}\n\n.lf-context__switch-hint {\n  display: block;\n  margin-top: 0.125rem;\n  font-size: 0.6875rem;\n  line-height: 1.5;\n  color: var(--muted-foreground);\n}\n\n.lf-context__stats {\n  display: grid;\n  grid-template-columns: repeat(2, 1fr);\n  gap: 0.0625rem;\n  flex-shrink: 0;\n  margin: 0;\n  padding: 0.75rem;\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-context__stats > div {\n  display: flex;\n  flex-direction: column;\n  gap: 0.0625rem;\n}\n\n.lf-context__stats dt {\n  font-size: 0.625rem;\n  font-weight: 500;\n  letter-spacing: 0.04em;\n  text-transform: uppercase;\n  color: var(--muted-foreground);\n}\n\n.lf-context__stats dd {\n  margin: 0;\n  font-family: var(--font-mono);\n  font-size: 0.9375rem;\n  font-weight: 600;\n  font-variant-numeric: tabular-nums;\n  color: var(--foreground);\n}\n\n.lf-context__warn {\n  color: var(--warning) !important;\n}\n\n.lf-context__list {\n  flex: 1;\n  min-height: 0;\n  overflow-y: auto;\n  padding: 0.5rem;\n}\n\n.lf-context__none {\n  padding: 1.5rem 0.75rem;\n  text-align: center;\n}\n\n.lf-context__none p {\n  margin: 0 0 0.75rem;\n  font-size: 0.75rem;\n  line-height: 1.6;\n  color: var(--muted-foreground);\n}\n\n.lf-context__cta {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  padding: 0.375rem 0.75rem;\n  font-size: 0.75rem;\n  font-weight: 600;\n  color: var(--primary-foreground);\n  background: var(--primary);\n  border: none;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n}\n\n.lf-context__row {\n  padding: 0.625rem;\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n}\n\n.lf-context__row + .lf-context__row {\n  margin-top: 0.375rem;\n}\n\n.lf-context__row-head {\n  display: flex;\n  align-items: baseline;\n  justify-content: space-between;\n  gap: 0.375rem;\n}\n\n.lf-context__row-name {\n  overflow: hidden;\n  font-size: 0.8125rem;\n  font-weight: 600;\n  color: var(--foreground);\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-context__row-version {\n  flex-shrink: 0;\n  font-family: var(--font-mono);\n  font-size: 0.6875rem;\n  color: var(--muted-foreground);\n}\n\n.lf-context__row-tags {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.25rem;\n  margin-top: 0.375rem;\n}\n\n.lf-context__row-tags .lf-badge {\n  font-size: 0.625rem !important;\n}\n\n.lf-context__row-meta {\n  margin: 0.375rem 0 0;\n  font-size: 0.6875rem;\n  line-height: 1.5;\n  color: var(--muted-foreground);\n}\n\n.lf-context__row-warn {\n  display: flex;\n  align-items: center;\n  gap: 0.3125rem;\n  margin: 0.3125rem 0 0;\n  font-size: 0.6875rem;\n  color: var(--warning);\n}\n\n.lf-context__row-ask {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  margin-top: 0.5rem;\n  padding: 0.25rem 0.5rem;\n  font-size: 0.6875rem;\n  font-weight: 500;\n  color: var(--primary);\n  background: transparent;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n  transition: background var(--transition-fast), border-color var(--transition-fast);\n}\n\n.lf-context__row-ask:hover {\n  background: var(--accent);\n  border-color: var(--primary);\n}\n\n/* ==========================================================================\n   Shortcuts dialog\n   ========================================================================== */\n\n.lf-modal {\n  position: fixed;\n  inset: 0;\n  z-index: 1080;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 1rem;\n  background: var(--overlay);\n  animation: lf-fade var(--transition) ease-out both;\n}\n\n@keyframes lf-fade {\n  from {\n    opacity: 0;\n  }\n  to {\n    opacity: 1;\n  }\n}\n\n.lf-modal__card {\n  width: 100%;\n  max-width: 26rem;\n  max-height: 85vh; /* fallback */\n  max-height: 85dvh;\n  overflow-y: auto;\n  background: var(--popover);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  box-shadow: var(--shadow-lg);\n}\n\n.lf-modal__head {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 0.5rem;\n  padding: 0.875rem 1rem;\n  border-bottom: 1px solid var(--border);\n}\n\n.lf-modal__title {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  margin: 0;\n  font-size: 0.9375rem;\n  font-weight: 600;\n  color: var(--popover-foreground);\n}\n\n.lf-modal__title i {\n  font-size: 0.8125rem;\n  color: var(--muted-foreground);\n}\n\n.lf-modal__close {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 1.875rem;\n  height: 1.875rem;\n  font-size: 0.8125rem;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: none;\n  border-radius: var(--radius-sm);\n  cursor: pointer;\n}\n\n.lf-modal__close:hover {\n  color: var(--foreground);\n  background: var(--secondary);\n}\n\n.lf-modal__list {\n  margin: 0;\n  padding: 0.5rem;\n  list-style: none;\n}\n\n.lf-modal__list li {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 1rem;\n  padding: 0.4375rem 0.5rem;\n  border-radius: var(--radius-sm);\n}\n\n.lf-modal__list li:hover {\n  background: var(--secondary);\n}\n\n.lf-modal__desc {\n  font-size: 0.8125rem;\n  color: var(--popover-foreground);\n}\n\n.lf-modal__keys {\n  display: flex;\n  flex-shrink: 0;\n  gap: 0.25rem;\n}\n\n.lf-modal__keys kbd {\n  min-width: 1.5rem;\n  padding: 0.125rem 0.375rem;\n  font-family: var(--font-mono);\n  font-size: 0.6875rem;\n  text-align: center;\n  color: var(--foreground);\n  background: var(--page-bg-subtle);\n  border: 1px solid var(--border);\n  border-bottom-width: 2px;\n  border-radius: var(--radius-sm);\n}\n\n/* ==========================================================================\n   Reflow\n   ========================================================================== */\n\n/* The context panel stops being affordable before the sidebar does: it is the\n   least essential of the three columns, so it overlays instead of squeezing\n   the conversation below a readable width. */\n@media (max-width: 1200px) {\n  .lf-assistant {\n    --lf-context-w: 18rem;\n  }\n}\n\n@media (max-width: 992px) {\n  .lf-assistant__context {\n    position: absolute;\n    inset: 0 0 0 auto;\n    z-index: 30;\n    width: min(20rem, 88vw);\n    flex-basis: auto;\n    box-shadow: var(--shadow-lg);\n  }\n}\n\n/* 768px matches .lf-hide-md / .lf-only-md in styles/responsive.css, so the\n   hamburger appears in exactly the same breath as the drawer takes over. */\n@media (max-width: 768px) {\n  .lf-assistant {\n    border-radius: var(--radius);\n  }\n\n  .lf-assistant__sidebar {\n    position: absolute;\n    inset: 0 auto 0 0;\n    z-index: 40;\n    flex-basis: auto;\n    width: min(17rem, 84vw);\n    transform: translateX(-100%);\n    transition: transform var(--transition);\n    box-shadow: var(--shadow-lg);\n  }\n\n  .lf-assistant__sidebar.is-open {\n    transform: none;\n  }\n\n  /* The desktop collapse must not survive into the drawer, or the drawer opens\n     at zero width. */\n  .lf-assistant__sidebar.is-collapsed {\n    flex-basis: auto;\n    width: min(17rem, 84vw);\n    border-right-color: var(--border);\n  }\n\n  .lf-sidebar {\n    width: 100%;\n  }\n\n  .lf-assistant__backdrop {\n    position: absolute;\n    inset: 0;\n    z-index: 35;\n    background: var(--overlay);\n  }\n}\n\n@media (max-width: 480px) {\n  .lf-msg__bubble {\n    max-width: 100%;\n  }\n\n  .lf-welcome__suggestions {\n    grid-template-columns: 1fr;\n  }\n}\n\n/* ==========================================================================\n   Dataset drop target\n\n   Wraps the conversation only. It takes over the flex role `.lf-assistant__body`\n   had in the column, and is the positioning context for the overlay.\n   ========================================================================== */\n\n.lf-drop {\n  position: relative;\n  flex: 1;\n  min-height: 0;\n  display: flex;\n  flex-direction: column;\n}\n\n.lf-drop__overlay {\n  position: absolute;\n  inset: 0;\n  z-index: 28;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 1rem;\n  background: var(--overlay);\n  backdrop-filter: blur(2px);\n}\n\n.lf-drop__card {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  gap: 0.5rem;\n  padding: 2rem 2.5rem;\n  text-align: center;\n  color: var(--foreground);\n  background: var(--card);\n  border: 2px dashed var(--primary);\n  border-radius: var(--radius-xl);\n  box-shadow: var(--shadow-lg);\n}\n\n.lf-drop__card i {\n  font-size: 1.75rem;\n  color: var(--primary);\n}\n\n.lf-drop__card strong {\n  font-size: 1rem;\n}\n\n.lf-drop__card span {\n  font-size: 0.8125rem;\n  color: var(--muted-foreground);\n}\n\n/* ==========================================================================\n   Build card\n\n   The only surface in the assistant that can create anything. It is deliberately\n   quiet until it has run: everything on it is a proposal.\n   ========================================================================== */\n\n.lf-msg--card {\n  max-width: var(--lf-measure);\n  margin: 0 auto 1.25rem;\n  width: 100%;\n}\n\n.lf-card {\n  display: flex;\n  flex-direction: column;\n  gap: 0.75rem;\n  padding: 1rem;\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-xl);\n  box-shadow: var(--shadow-sm);\n}\n\n.lf-card__head {\n  display: flex;\n  align-items: center;\n  gap: 0.625rem;\n}\n\n.lf-card__badge {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: 2rem;\n  height: 2rem;\n  font-size: 0.875rem;\n  color: var(--primary-foreground);\n  background: var(--primary);\n  border-radius: var(--radius);\n}\n\n.lf-card__heading {\n  display: flex;\n  flex-direction: column;\n  min-width: 0;\n  flex: 1;\n}\n\n.lf-card__heading strong {\n  font-size: 0.9375rem;\n  color: var(--foreground);\n}\n\n.lf-card__sub {\n  overflow: hidden;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-card__mode {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  flex-shrink: 0;\n  padding: 0.25rem 0.625rem;\n  font-size: 0.6875rem;\n  font-weight: 500;\n  color: var(--primary);\n  background: var(--primary-soft);\n  border-radius: var(--radius-full);\n}\n\n.lf-card__facts {\n  display: grid;\n  grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));\n  gap: 0.625rem;\n  margin: 0;\n  padding: 0.75rem;\n  background: var(--secondary);\n  border-radius: var(--radius-lg);\n}\n\n.lf-card__facts div {\n  min-width: 0;\n}\n\n.lf-card__facts dt {\n  margin: 0 0 0.125rem;\n  font-size: 0.6875rem;\n  font-weight: 500;\n  color: var(--muted-foreground);\n}\n\n.lf-card__facts dd {\n  margin: 0;\n  overflow: hidden;\n  font-size: 0.875rem;\n  font-weight: 600;\n  color: var(--foreground);\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-card__hint {\n  margin: 0;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n.lf-card__classes {\n  display: flex;\n  flex-wrap: wrap;\n  align-items: center;\n  gap: 0.375rem;\n}\n\n.lf-card__chip {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  padding: 0.1875rem 0.5rem;\n  font-size: 0.75rem;\n  color: var(--foreground);\n  background: var(--secondary);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-full);\n}\n\n.lf-card__chip em {\n  font-family: var(--font-mono);\n  font-size: 0.6875rem;\n  font-style: normal;\n  color: var(--muted-foreground);\n}\n\n/*\n * `--card`, not `--background`: the global layer forces `background: var(--card)`\n * on `input:focus` with `!important`, so declaring anything else here made the\n * field visibly change shade the moment it was clicked. Matching it means no\n * flip. The focus border and ring also come from that layer, which is why there\n * is no `:focus` rule here — the one that used to be here was dead.\n */\n.lf-card__rename {\n  width: 8rem;\n  padding: 0.25rem 0.5rem;\n  font-family: inherit;\n  font-size: 0.75rem;\n  color: var(--foreground);\n  background: var(--card);\n  border: 1px solid var(--input);\n  border-radius: var(--radius-sm);\n}\n\n.lf-card__mini {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  padding: 0.1875rem 0.5rem;\n  font-size: 0.6875rem;\n  font-weight: 500;\n  color: var(--muted-foreground);\n  background: transparent;\n  border: 1px dashed var(--border-strong);\n  border-radius: var(--radius-full);\n  cursor: pointer;\n}\n\n.lf-card__mini:hover {\n  color: var(--primary);\n  border-color: var(--primary);\n}\n\n.lf-card__notes {\n  display: flex;\n  flex-direction: column;\n  gap: 0.25rem;\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n\n.lf-card__notes li {\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n.lf-card__notes i {\n  color: var(--info);\n}\n\n.lf-card__error {\n  display: flex;\n  align-items: flex-start;\n  gap: 0.375rem;\n  margin: 0;\n  padding: 0.5rem 0.625rem;\n  font-size: 0.75rem;\n  color: var(--destructive);\n  background: var(--destructive-soft);\n  border-radius: var(--radius);\n}\n\n.lf-card__result {\n  display: flex;\n  align-items: center;\n  gap: 0.375rem;\n  margin: 0;\n  padding: 0.5rem 0.625rem;\n  font-size: 0.8125rem;\n  font-weight: 500;\n  color: var(--success);\n  background: var(--secondary);\n  border-radius: var(--radius);\n}\n\n.lf-card__result--warn {\n  color: var(--warning);\n  background: var(--warning-soft);\n}\n\n.lf-card__foot {\n  display: flex;\n  flex-wrap: wrap;\n  align-items: center;\n  gap: 0.5rem;\n}\n\n.lf-card__run,\n.lf-card__stop {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.4375rem;\n  padding: 0.4375rem 0.875rem;\n  font-size: 0.8125rem;\n  font-weight: 600;\n  border: none;\n  border-radius: var(--radius);\n  cursor: pointer;\n  transition: background var(--transition-fast);\n}\n\n.lf-card__run {\n  color: var(--primary-foreground);\n  background: var(--primary);\n}\n\n.lf-card__run:hover {\n  background: var(--primary-hover);\n}\n\n.lf-card__stop {\n  color: var(--destructive-foreground);\n  background: var(--destructive);\n}\n\n.lf-card__stop:hover {\n  background: var(--destructive-hover);\n}\n\n.lf-card__link {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.375rem;\n  padding: 0.4375rem 0.5rem;\n  font-size: 0.8125rem;\n  font-weight: 500;\n  color: var(--primary);\n  background: transparent;\n  border: none;\n  cursor: pointer;\n}\n\n.lf-card__link:hover {\n  text-decoration: underline;\n}\n\n/* ---- the five steps ---- */\n\n.lf-steps {\n  display: flex;\n  flex-direction: column;\n  gap: 0.25rem;\n  margin: 0;\n  padding: 0.625rem;\n  list-style: none;\n  background: var(--secondary);\n  border-radius: var(--radius-lg);\n}\n\n.lf-steps__row {\n  display: grid;\n  grid-template-columns: 1rem 1fr auto;\n  align-items: center;\n  gap: 0.5rem;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n.lf-steps__row--done .lf-steps__icon {\n  color: var(--success);\n}\n\n.lf-steps__row--running {\n  color: var(--foreground);\n}\n\n.lf-steps__row--running .lf-steps__icon {\n  color: var(--primary);\n}\n\n.lf-steps__row--failed {\n  color: var(--destructive);\n}\n\n.lf-steps__row--failed .lf-steps__icon {\n  color: var(--destructive);\n}\n\n.lf-steps__row--skipped {\n  opacity: 0.55;\n}\n\n.lf-steps__icon {\n  font-size: 0.75rem;\n  text-align: center;\n}\n\n.lf-steps__label {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.lf-steps__state {\n  font-family: var(--font-mono);\n  font-size: 0.6875rem;\n}\n\n.lf-steps__bar {\n  grid-column: 2 / -1;\n  height: 3px;\n  overflow: hidden;\n  background: var(--border);\n  border-radius: var(--radius-full);\n}\n\n.lf-steps__fill {\n  display: block;\n  height: 100%;\n  background: var(--primary);\n  border-radius: var(--radius-full);\n  transition: width var(--transition);\n}\n\n@media (max-width: 640px) {\n  .lf-card__facts {\n    grid-template-columns: repeat(2, 1fr);\n  }\n}\n\n/* A format we recognise but do not import yet — said plainly, on the chip. */\n.lf-attach__warn {\n  color: var(--warning);\n}\n\n/* ==========================================================================\n   The interview\n\n   One card that asks, shows what it already knows, and confirms once.\n   ========================================================================== */\n\n.lf-answers {\n  display: flex;\n  flex-direction: column;\n  gap: 0.25rem;\n  margin: 0;\n  padding: 0.5rem 0.625rem;\n  list-style: none;\n  background: var(--secondary);\n  border-radius: var(--radius-lg);\n}\n\n.lf-answers li {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  font-size: 0.75rem;\n}\n\n.lf-answers__label {\n  min-width: 5rem;\n  color: var(--muted-foreground);\n}\n\n.lf-answers__value {\n  flex: 1;\n  font-weight: 600;\n  color: var(--foreground);\n}\n\n.lf-answers__from {\n  font-size: 0.6875rem;\n  font-style: normal;\n  color: var(--muted-foreground);\n}\n\n.lf-answers__edit {\n  padding: 0.125rem 0.4375rem;\n  font-size: 0.6875rem;\n  color: var(--primary);\n  background: transparent;\n  border: 1px solid var(--primary-soft);\n  border-radius: var(--radius-full);\n  cursor: pointer;\n}\n\n.lf-answers__edit:hover {\n  background: var(--primary-soft);\n}\n\n.lf-ask {\n  display: flex;\n  flex-direction: column;\n  gap: 0.5rem;\n}\n\n.lf-ask__prompt {\n  margin: 0;\n  font-size: 0.9375rem;\n  font-weight: 600;\n  color: var(--foreground);\n}\n\n/* The only place in the flow that quotes a cost — because it is the only\n   answer that commits the user to GPU time. */\n.lf-ask__cost {\n  display: flex;\n  align-items: center;\n  gap: 0.375rem;\n  margin: 0;\n  padding: 0.375rem 0.5rem;\n  font-size: 0.75rem;\n  color: var(--warning);\n  background: var(--warning-soft);\n  border-radius: var(--radius);\n}\n\n.lf-ask__options {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.375rem;\n}\n\n.lf-ask__option {\n  padding: 0.4375rem 0.75rem;\n  font-size: 0.8125rem;\n  font-weight: 500;\n  color: var(--foreground);\n  background: var(--background);\n  border: 1px solid var(--border-strong);\n  border-radius: var(--radius-full);\n  cursor: pointer;\n  transition: border-color var(--transition-fast), background var(--transition-fast);\n}\n\n.lf-ask__option:hover {\n  color: var(--primary);\n  background: var(--primary-soft);\n  border-color: var(--primary);\n}\n\n.lf-review,\n.lf-confirm {\n  display: flex;\n  flex-direction: column;\n  gap: 0.625rem;\n}\n\n.lf-review__coverage {\n  margin: 0;\n  font-size: 0.8125rem;\n  font-weight: 600;\n  color: var(--success);\n}\n\n.lf-review__coverage--warn {\n  color: var(--warning);\n}\n\n.lf-confirm__lines {\n  display: flex;\n  flex-direction: column;\n  gap: 0.375rem;\n  margin: 0;\n  padding: 0.75rem;\n  list-style: none;\n  font-size: 0.8125rem;\n  color: var(--foreground);\n  background: var(--secondary);\n  border-radius: var(--radius-lg);\n}\n\n.lf-confirm__lines i {\n  width: 1rem;\n  color: var(--primary);\n}\n\n.lf-confirm__names {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n.lf-confirm__names label {\n  display: flex;\n  flex-direction: column;\n  gap: 0.25rem;\n  font-size: 0.6875rem;\n  color: var(--muted-foreground);\n}\n\n.lf-confirm__names .lf-card__rename {\n  width: 12rem;\n}\n\n/* ==========================================================================\n   The busy strip\n\n   Named, measured, alive and escapable — the four things the interview card\n   was missing while it worked, which is why a working import read as a frozen\n   one and sent the user to the Change button.\n   ========================================================================== */\n\n.lf-busy {\n  display: flex;\n  flex-direction: column;\n  gap: 0.5rem;\n  padding: 0.75rem;\n  background: var(--secondary);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n}\n\n.lf-busy__line {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  font-size: 0.8125rem;\n  color: var(--foreground);\n}\n\n.lf-busy__spin {\n  flex-shrink: 0;\n  color: var(--primary);\n}\n\n.lf-busy__label {\n  flex: 1 1 auto;\n  min-width: 0;\n}\n\n/* Tabular figures so a climbing percentage does not jiggle its neighbours. */\n.lf-busy__percent,\n.lf-busy__elapsed {\n  flex-shrink: 0;\n  font-variant-numeric: tabular-nums;\n  font-size: 0.75rem;\n}\n\n.lf-busy__percent {\n  color: var(--primary);\n  font-weight: 600;\n}\n\n.lf-busy__elapsed {\n  color: var(--muted-foreground);\n}\n\n.lf-busy__detail {\n  margin: 0;\n  font-size: 0.75rem;\n  color: var(--muted-foreground);\n}\n\n.lf-busy__track {\n  position: relative;\n  height: 4px;\n  overflow: hidden;\n  background: var(--border);\n  border-radius: var(--radius-full);\n}\n\n.lf-busy__fill {\n  display: block;\n  height: 100%;\n  background: var(--primary);\n  border-radius: var(--radius-full);\n  transition: width var(--transition);\n}\n\n/*\n * No real percentage exists for this step, so the bar sweeps instead of\n * filling. Deliberately NOT a fabricated number: a bar that parks at 90%\n * promises an ending it cannot deliver, and teaches the user to distrust every\n * bar in the product.\n */\n.lf-busy__track--indeterminate .lf-busy__fill {\n  position: absolute;\n  width: 35%;\n  animation: lf-busy-sweep 1.4s ease-in-out infinite;\n}\n\n@keyframes lf-busy-sweep {\n  from { left: -35%; }\n  to { left: 100%; }\n}\n\n.lf-busy__stop {\n  align-self: flex-start;\n  min-height: 2.25rem;\n  padding: 0.375rem 0.75rem;\n  font-size: 0.75rem;\n  color: var(--foreground);\n  background: var(--background);\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  cursor: pointer;\n  transition: border-color var(--transition), color var(--transition);\n}\n\n.lf-busy__stop:hover {\n  color: var(--destructive);\n  border-color: var(--destructive);\n}\n\n/* The elapsed counter is the one live signal a stalled tab cannot fake, so it\n   stays; only the sweep is motion worth suppressing. */\n@media (prefers-reduced-motion: reduce) {\n  .lf-busy__track--indeterminate .lf-busy__fill {\n    animation: none;\n    left: 0;\n    width: 100%;\n    opacity: 0.45;\n  }\n}\n\n/* A busy card is visibly a different state, not the same card with a spinner. */\n.lf-card--busy {\n  border-color: var(--primary);\n}\n\n/*\n * Disabled controls on a card.\n *\n * There was no :disabled rule anywhere in this file, so every `disabled`\n * button rendered at full strength with a normal cursor — it looked pressable,\n * did nothing when pressed, and gave no reason. That is worse than no button.\n */\n.lf-answers__edit:disabled,\n.lf-card__run:disabled,\n.lf-card__link:disabled,\n.lf-card__mini:disabled,\n.lf-busy__stop:disabled {\n  opacity: 0.45;\n  cursor: not-allowed;\n  pointer-events: auto; /* keep the title tooltip reachable */\n}\n\n.lf-answers__edit:disabled:hover,\n.lf-card__run:disabled:hover,\n.lf-card__link:disabled:hover,\n.lf-card__mini:disabled:hover {\n  filter: none;\n  border-color: var(--border);\n}\n\n/* A run that created nothing reads as a failure, not as a near-miss. */\n.lf-card__result--bad {\n  color: var(--destructive);\n}\n\n.lf-card__hint--reason {\n  color: var(--muted-foreground);\n  font-style: italic;\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
